import React from "react";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { withStyles } from "@mui/styles";
// import "./fonts/AktivGrotesk.css";

const Theme = ({ children, isAdminMode = false, isOverviewMode = false }) => {
  const styles = children.props.styles;

  const baseColors = {
    primary: styles.colors.primary,
  };

  const adminColors = {
    primary: "#018CA5",
  };

  const overviewColors = {
    primary: "#3D5A80",
  };

  let currentColors = baseColors;

  if (isOverviewMode) {
    currentColors = overviewColors;
  } else if (isAdminMode) {
    currentColors = adminColors;
  }

  const theme = createTheme({
    typography: {
      fontFamily: "Inter !important",
      fontSize: styles.body.fontSize,
      backgroundColor: styles.body.background,
    },
    palette: {
      primary: { main: currentColors.primary },
      secondary: { main: styles.colors.secondary },
      white: { main: "#FFFFFF" },
      textColor: { main: styles.textColor },
      text: { primary: styles.textColor },
      inputBorderColor: { primary: styles.inputBorderColor },
      blackSaveButton: {
        main: "#333333",
        contrastText: "#fff",
      },
    },
    props: {
      MuiTable: {
        component: "div",
      },
      MuiTableRow: {
        component: "div",
      },
      MuiTableHead: {
        component: "div",
      },
      MuiTableBody: {
        component: "div",
      },
      MuiTableCell: {
        component: "div",
      },
    },

    components: {
      MuiDialog: {
        defaultProps: {
          BackdropProps: {
            sx: { backgroundColor: "#13526580" },
          },
        },
        styleOverrides: {
          paper: { borderRadius: 12 },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: ({ theme }) => ({ fontWeight: 700, padding: theme.spacing(2) }),
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: ({ theme }) => ({ padding: theme.spacing(2) }),
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: ({ theme }) => ({ padding: theme.spacing(2) }),
        },
      },
      // Removed global Backdrop override so Select/Menu etc. don't inherit it
    },

    overrides: {
      MuiSwitch: {
        root: {
          "&$disabled": {},
        },
        switchBase: {
          "&$checked": {},
        },
        track: {
          "&$checked": {
            backgroundColor: "red", // Background color when checked
          },
        },
        thumb: {
          "&$checked": {
            backgroundColor: "red", // Background color when checked
          },
        },
      },
      MuiButton: {
        root: { textTransform: "none", fontFamily: styles.body.fontFamily },
        contained: { color: currentColors.primary, backgroundColor: "#f7f7f7" },
        containedPrimary: {
          backgroundColor: styles.colors.accentColor,
          "&:hover": { backgroundColor: styles.colors.accentColorHover },
        },
        containedSecondary: {
          backgroundColor: styles.colors.secondaryButton,
          color: "white",
          "&:hover": { backgroundColor: styles.colors.secondaryButtonHover },
        },
        outlined: { borderColor: currentColors.primary },
        outlinedSecondary: {
          borderColor: styles.colors.secondaryDark,
          color: styles.colors.secondaryDark,
        },
      },
      MuiAvatar: {
        root: {
          MuiIcon: { root: { color: "white" } },
        },
        colorDefault: {
          backgroundColor: "#154153",
          color: "white",
        },
      },
      MuiTypography: {
        h1: {
          color: styles.headingsColor,
          fontFamily: styles.fontsHeadings,
          letterSpacing: "-1px",
          fontSize: "2rem",
        },
        h2: {
          color: styles.headingsColor,
          fontFamily: styles.fontsHeadings,
          letterSpacing: "-1px",
          fontSize: "1.4rem",
        },
        h3: {
          color: styles.headingsColor,
          fontFamily: styles.fontsHeadings,
          letterSpacing: "-1px",
          fontSize: "1.2rem",
        },
        h4: {
          color: styles.headingsColor,
          fontFamily: styles.fontsHeadings,
          letterSpacing: "-1px",
          fontSize: "1.1rem",
          fontWeight: 500,
        },
        h5: {
          color: styles.headingsColor,
          fontFamily: styles.fontsHeadings,
          letterSpacing: "-0.5px",
          fontSize: "1rem",
        },
        h6: {
          color: styles.headingsColor,
          fontFamily: styles.fontsHeadings,
          letterSpacing: "normal",
          lineHeight: "1.2",
          fontSize: 16,
        },
        body1: { fontFamily: styles.fonts },
      },
      MuiPaper: { rounded: { borderRadius: 12 } },
      MuiTableHead: {
        root: { backgroundColor: styles.tableHeadBgColor },
      },

      MuiTableCell: {
        root: { padding: "12px 5px", fontFamily: styles.tableHeadFontFamily },
        head: {
          fontWeight: 600,
          fontSize: styles.tableHeadSize,
          padding: "8px 5px",
        },
      },
      MuiCardActions: {
        root: { padding: 16 },
      },
      MuiChip: {
        root: {
          color: styles.chipColor,
          backgroundColor: styles.chipBgColor,
        },
      },
      MuiListItemIcon: {
        root: {
          color: currentColors.primary,
        },
      },
      MuiListItem: { button: { color: currentColors.primary } },
      MuiTabs: {
        root: {
          color: currentColors.primary,
          marginBottom: 0,
          borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
        },
      },
      MuiTab: {
        root: {
          color: currentColors.primary,
          fontFamily: styles.body.fontFamily,
        },
        textColorPrimary: { color: currentColors.primary },
      },

      MuiAlert: {
        standardWarning: {
          color: styles.standardWarningColor,
          backgroundColor: styles.standardWarningBg,
          border: "1px solid",
          borderColor: styles.standardWarningIcon,
        },
        filledInfo: {
          color: styles.standardInfoColor,
          backgroundColor: styles.standardInfoBg,
        },
        standardInfo: {
          color: styles.standardInfoColor,
          backgroundColor: styles.standardInfoBg,
        },

        standardError: {
          color: styles.standardErrorColor,
          backgroundColor: styles.standardErrorBg,
          border: "1px solid",
          borderColor: styles.standardErrorIcon,
        },
      },
      MuiAutocomplete: {
        option: { borderBottom: "1px solid rgba(0, 0, 0, 0.1)" },
      },
    },
  });

  const drawerWidth = 210;

  const GlobalCSS = withStyles({
    "@global": {
      body: {
        margin: 0,
        padding: 0,
        backgroundColor: styles.body.background,
        color: styles.body.color,
        fontFamily: styles.body.fontFamily,
      },

      ".environmentAndy": {
        borderTop: "4px solid #ff7777ff",
        borderBottom: 0,
      },

      //Overrides to turn product wizard dividers into individual sections
      ".products": {
        "& .generalModuleCardContainer": {
          "& .mainBodyModulePaper": {
            padding: 0,
            border: "none",
            borderRadius: 0,
            backgroundColor: "unset",
            boxShadow: "unset",
            position: "relative",
            zIndex: 2,
            "&.isSubformExpanded": {
              "& .sectionComponent": {
                backgroundColor: "#f8f8f8 !important",
                pointerEvents: "none",
              },
              "& .componentSectionCreateNew": {
                backgroundColor: "#fff !important",
                pointerEvents: "auto",
                "& .sectionComponent": {
                  backgroundColor: "#fff !important",
                  pointerEvents: "auto",
                },
              },
            },
            "& >.sectionComponent": {
              padding: "8px 15px",
              backgroundColor: "#ffffff",
              marginBottom: 0,
              position: "relative",
              border: "none",

              "&::before": {
                boxShadow:
                  "0px 3px 1px -2px rgba(0, 0, 0, 0.2), 0px 2px 2px 0px rgba(0, 0, 0, 0.14), 0px 1px 5px 0px rgba(0, 0, 0, 0.12)",
                content: '""',
                position: "absolute",
                top: 0,
                bottom: 0,
                left: 0,
                right: 0,
                zIndex: "-1",
              },

              "&.withDivider": {
                marginBottom: 12,
              },

              "&.withDivider, &:last-of-type, &:has(+:last-of-type:not(.sectionComponent))":
                {
                  borderBottomRightRadius: 16,
                  borderBottomLeftRadius: 16,
                  paddingBottom: 15,
                  "&::before": {
                    borderBottomRightRadius: 16,
                    borderBottomLeftRadius: 16,
                  },
                },

              "&.withDivider+.MuiCollapse-root+.sectionComponent, &.withTopDivider":
                {
                  marginTop: 12,
                },

              "&.withDivider+.MuiCollapse-root+.sectionComponent, &.withTopDivider, &:first-of-type, &.withDivider+.sectionComponent":
                {
                  borderTopRightRadius: 16,
                  borderTopLeftRadius: 16,
                  paddingTop: 15,
                  "&::before": {
                    borderTopRightRadius: 16,
                    borderTopLeftRadius: 16,
                  },
                },

              "&:not(.withDivider)": {
                "&:has(+.MuiCollapse-root), &:has(+.sectionComponent:not(.withTopDivider))":
                  {
                    borderBottomRightRadius: 0,
                    borderBottomLeftRadius: 0,
                    "&::before": {
                      borderBottomRightRadius: 0,
                      borderBottomLeftRadius: 0,
                    },
                  },
                "&:has(+.withTopDivider)": {
                  borderBottomRightRadius: 16,
                  borderBottomLeftRadius: 16,
                  paddingBottom: 15,
                  "&::before": {
                    borderBottomRightRadius: 16,
                    borderBottomLeftRadius: 16,
                  },
                },
              },

              "&+.MuiCollapse-root:not(.MuiCollapse-hidden)": {
                padding: 15,
                backgroundColor: "white",
                position: "relative",
                "&::before": {
                  boxShadow:
                    "0px 3px 1px -2px rgba(0, 0, 0, 0.2), 0px 2px 2px 0px rgba(0, 0, 0, 0.14), 0px 1px 5px 0px rgba(0, 0, 0, 0.12)",
                  content: '""',
                  position: "absolute",
                  top: 0,
                  bottom: 0,
                  left: 0,
                  right: 0,
                  zIndex: "-1",
                },
              },
            },
          },
        },
      },

      ".switchesLabel": {
        color: styles.inputsLabelColor,
        transform: "none",
        whiteSpace: "nowrap",
        fontSize: styles.inputsLabelFontSize,
        fontWeight: styles.inputsLabelFontWeight,
        textOverflow: "ellipsis",
        overflow: "hidden",
        width: "100%",
        lineHeight: 1.2,
        fontFamily: styles.body.fontFamily,
      },
      ".titleLabel": {
        color: styles.inputsLabelColor,
        transform: "none",
        whiteSpace: "nowrap",
        fontSize: styles.inputsLabelFontSize,
        fontWeight: styles.inputsLabelFontWeight,
        textOverflow: "ellipsis",
        overflow: "hidden",
        width: "100%",
        lineHeight: 1.2,
        fontFamily: styles.body.fontFamily,
      },
      ".titleLabelEdit": {
        paddingBottom: "15px",
      },

      ".w-md-editor-content": {
        backgroundColor: styles.inputBgColor,
        color: styles.inputColor,
      },
      ".w-md-editor-preview": {
        backgroundColor: styles.inputBgColorDisabled,
        color: styles.inputColor,
        "& p": {
          fontFamily: styles.body.fontFamily,
        },
      },
      ".MuiInputBase-root": {
        backgroundColor: styles.inputBgColor,
        color: styles.inputColor,

        "&.Mui-disabled": {
          color: styles.inputColor,
          border: "none",
          padding: "0!important",

          "& .MuiOutlinedInput-notchedOutline": { borderColor: "transparent" },
          "& .MuiSelect-outlined": { borderColor: "transparent" },
          "& textarea": {
            border: "1px solid",
            padding: 12,
            borderColor: styles.textAreaColor,
            borderRadius: 4,
          },
          "& input": {
            padding: "10px !important",
            border: "none",
            borderColor: styles.inputBorderColorDisabled,
            borderRadius: 4,
          },
        },
        "& input": {
          "&::placeholder": {
            fontStyle: styles.placeholderStyle,
            fontSize: styles.placeholderFontSize,
            color: styles.placeholderColor,
            opacity: 1,
          },
        },

        "& textarea": {
          "&::placeholder": {
            fontStyle: styles.placeholderStyle,
            fontSize: styles.placeholderFontSize,
            color: styles.placeholderColor,
            opacity: 1,
          },
        },
      },
      ".MuiFormControl-root Select-SimpleSelectCurrentPayout": {
        paddingTop: "0px !important",
      },

      ".Select-SimpleSelect": {
        "& .MuiPaper-root": {
          maxHeight: "340px",
        },
      },
      ".Select-MultipleSelect": {
        "& .MuiInputLabel-outlined": {
          transform: "translate(14px, 42px) scale(1);",
        },
        "&.SelectSmall": {
          "& .MuiInputLabel-outlined": {
            transform: "translate(0px, 40px) scale(1);",
          },
        },
        "&.MuiInput-underline:before": {
          border: "none",
        },
        "&.MuiInput-underline:hover:not(.Mui-disabled):before": {
          border: "none",
        },
        "& .MuiPaper-root": {
          maxHeight: "340px",
        },
        "& .MuiSelect-selectMenu": {
          borderColor: "rgba(0, 0, 0, 0.23)",
          border: "1px solid",
          paddingLeft: "14px",
          paddingTop: "18.5px",
          paddingBottom: "18.5px",
        },
        "& .MuiInputLabel-shrink": {
          transform: "none",
        },
        "& .MuiInput-formControl": {
          marginTop: 0,
        },
      },
      ".SelectSmall": {
        "& .MuiSelect-selectMenu": {
          paddingTop: 10.5,
          paddingBottom: 10.5,
        },
      },
      ".MuiAlert-outlinedWarning": { backgroundColor: "white" },
      ".wrapper": { display: "flex" },
      ".appBar": {
        zIndex: theme.zIndex.drawer + 1,
      },
      ".appBarShift": {
        "& .transparentButton": {
          display: "inline-block",
          fontSize: "0.875rem", // text-sm
          padding: "0.5rem 1rem", // px-4 py-2
          lineHeight: "1", // leading-none
          borderRadius: "0.375rem", // border-radius (rounded)
          color: "white",
          border: "1px solid white", // border-white
          transition: theme.transitions.create(
            ["border", "color", "background-color"],
            {
              easing: theme.transitions.easing.easeInOut,
              duration: theme.transitions.duration.standard,
            },
          ),
          marginTop: "1rem", // mt-4
          marginRight: "1rem", // mt-4
          textTransform: "none",
          "&:hover": {
            borderColor: "transparent", // hover:border-transparent
            color: "#154153", // hover:text-tc-main (You can adjust this color.)
            backgroundColor: "white", // hover:bg-white
          },
          [theme.breakpoints.up("lg")]: {
            marginTop: 0, // lg:mt-0
          },
        },
      },
      ".drawerPaper": {
        "&:has(.expanded)": {
          scrollbarGutter: "stable",
        },
        height: "calc(100vh - 64px) !important",
        overflowY: "auto !important",
        overflowX: "hidden !important",
        marginTop: "64px !important",
        direction: "rtl !important",
        scrollbarWidth: "thin !important",

        position: "relative !important",
        whiteSpace: "nowrap !important",
        width: drawerWidth,
        boxShadow: "0px 1px 3px 0px #bdbdbd !important",
        border: "none !important",
        transition: theme.transitions.create("width", {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen,
        }),
      },
      ".drawerPaperClose": {
        overflowX: "hidden",
        transition: theme.transitions.create("width", {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.leavingScreen,
        }),
        width: theme.spacing(7),
        [theme.breakpoints.up("sm")]: {
          width: theme.spacing(7),
        },
      },
      ".menuButton": {
        marginRight: 36,
      },
      ".menuButtonHidden": {
        display: "none",
      },
      ".menuIsologo": {
        marginRight: 22,
        marginLeft: 8,
      },
      ".menuIsologoHidden": {
        display: "none",
      },
      ".LayoutContainer": {
        paddingTop: theme.spacing(4),
        paddingBottom: theme.spacing(4),
      },
      ".paperDrawer": {
        padding: theme.spacing(2),
        display: "flex",
        overflow: "auto",
        flexDirection: "column",
      },
      // Main Navigation Top Bar - navBarTopContainer
      ".navBarTopContainer": {
        "& .welcomeText": {
          fontWeight: 400,
          marginRight: 15,
        },
      },
      ".app_bar_spacer": theme.mixins.toolbar,
      ".MainLayout_wrapper": {
        display: "flex",
        "& .toolbar_Appbar": {
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          padding: "0px 16px 0px 18px",
          ...theme.mixins.toolbar,
        },
        "& .toolbarDrawer": {
          alignItems: "center",
          justifyContent: "flex-end",
          ...theme.mixins.toolbar,
        },
        "& .toolbarDrawerSidebar": {
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          padding: "0 8px",
          ...theme.mixins.toolbar,
        },
        "& .drawerPaperClose .toolbarDrawerButton": {
          left: "56px !important",
          right: "unset",
          borderRadius: "0 100px 100px 0",
          padding: "8px 2px 8px 0px",
          "&:hover": {
            paddingLeft: "3px",
          },
        },
        "&:not(.drawerPaperClose) .toolbarDrawerButton": {
          "&:hover": {
            paddingLeft: "3px",
          },
        },
        "& .toolbarDrawerButton": {
          position: "fixed",
          left: "210px",
          top: "71px",
          borderRadius: "0 100px 100px 0",
          padding: "8px 2px 8px 0px",
          right: "unset",
          transition: "0.1s",
          "& .MuiSvgIcon-root": {
            position: "relative",
            left: "-2px",
          },
        },
        "& .toolbarLogo": {
          maxHeight: "35px",
          marginLeft: 0,
          marginRight: "auto",
          display: "none",
        },
        "& .toolbarLogoShow": {
          display: "block",
        },
        "& .main_admin": {
          flexGrow: 1,
          height: "100vh",
          overflow: "hidden",
        },
        "& .title_Dashboard": {
          flexGrow: 1,
          marginLeft: 20,
          fontSize: 24,
          fontWeight: 400,
        },
      },
      ".main": { flexGrow: 1, height: "100vh", overflow: "auto" },
      ".login": {
        backgroundColor: "white",
        textAlign: "left",

        "& .title": {
          marginBottom: 0,
          color: "#333333",
        },

        "& .form-header": {
          marginBottom: theme.spacing(4),
        },
      },
      ".login_container": {
        padding: theme.spacing(4),
      },
      ".bg-login": {
        backgroundColor: currentColors.primary,
        backgroundPosition: "center",
        backgroundSize: "cover",
        "& .logo": {
          display: "block",
          position: "relative",
          top: "60px",
          maxWidth: "230px !important",
        },
      },

      ".paper": {
        padding: theme.spacing(2),
        display: "flex",
        flexDirection: "column",
      },
      ".container": {
        paddingTop: theme.spacing(4),
        paddingBottom: theme.spacing(4),
      },
      ".mainLayout_container": {
        paddingTop: theme.spacing(2.5),
        overflowX: "hidden",
        height: "calc(100% - 64px)",
        padding: "0!important",
      },
      ".content_section": {
        backgroundColor: "rgba(255,255,255,.5)",
      },
      ".filters_section_container": {
        display: "flex",
        alignItems: "center",
        marginBottom: theme.spacing(3),
        padding: 0,
      },
      ".filters_section_left": { flexGrow: 1 },
      ".filters_section_actions": {
        marginRight: 0,
      },
      ".filter_tablecell": {
        paddingBottom: "18px !important",

        "& .MuiFormControl-root": {
          paddingTop: 0,
          marginBottom: 0,
        },
        "& .MuiFormLabel-root": {
          display: "none",
        },
      },
      ".filter_tablecell_open": {
        paddingBottom: "0px !important",

        "& .MuiFormControl-root": {
          paddingTop: 0,
          marginBottom: 0,
        },
      },
      ".MenuHeader_Avatar": {
        backgroundColor: "rgba(255,255,255,.2) !important",
      },
      ".MenuHeader_Badge": {
        "& .MuiBadge-badge": { backgroundColor: "orange" },
      },
      ".badgeError": { backgroundColor: "red", color: "white" },
      ".badgeAlert": { backgroundColor: "orange", color: "white" },
      ".button_lowecase": {
        "& .MuiTypography-root": { textTransform: "none" },
      },
      ".MenuLogin": {
        display: "flex",
      },
      ".Detail_Action_ViewHeader": {
        backgroundColor: "#f5f5f5",
        paddingTop: "7px",
        paddingBottom: "7px",
      },
      ".Detail_Action_ViewContainer": {
        paddingTop: theme.spacing("0.5"),
        paddingRight: theme.spacing(2),
        paddingBottom: theme.spacing(2),
        paddingLeft: theme.spacing(2),
        "& .MuiTableHead-root": {
          backgroundColor: "#f2f6f7",
        },
        "& .MuiTableCell-head": {
          border: "none",
          padding: "8px 8px",
        },
        "& .MuiTableCell-root": {
          fontFamily: "inherit",
        },
        "& .typographyHome": {
          fontWeight: 200,
          color: "#333333",
          fontSize: "0.9rem",
          marginBottom: "10px",
        },
        "& .cardHome": {
          backgroundColor: "#cae4f9",
          padding: "5px",
          display: "flex",
          flexDirection: "column",
          height: "100%",
          "& .buttonCardHome": {
            marginTop: "auto",
            margin: "10px",
            width: "auto",
            alignSelf: "flex-start",
            display: "flex",
            alignItems: "center",
            textTransform: "none",
          },
        },
      },
      ".Detail_Action_ViewContainer_KnowledgeBase": {
        padding: theme.spacing(2),
        flex: 1,
        display: "flex",
        flexDirection: "column",
        height: "100%",

        "& .MuiTableHead-root": {
          backgroundColor: "#f2f6f7",
        },
        "& .MuiTableCell-head": {
          border: "none",
          padding: "8px 8px",
        },
        "& .MuiTableCell-root": {
          fontFamily: "inherit",
        },
      },
      ".DashboardCardContracted": {
        "& .MuiCardHeader-action": { margin: 0 },
        "& .icon": {
          padding: 8,
          display: "block",
          width: 40,
          height: 40,
          borderRadius: "50%",
        },
        "&:hover": {
          "& .icon": {
            backgroundColor: "#f0f0f0",
          },
        },
      },
      ".chipLastLogin": {
        float: "right",
      },
      ".dialogTitle_root": { margin: 0, padding: theme.spacing(2) },
      ".dialogTitle_closeButton": {
        position: "absolute",
        right: theme.spacing(0.5),
        top: theme.spacing(0.5),
        color: theme.palette.grey[500],
      },

      ".dialogTitle_center": {
        position: "absolute",
        right: "80px",
        top: theme.spacing(1.5),
        color: theme.palette.grey[500],
      },

      ".dialogActions_root": {
        margin: 0,
        padding: theme.spacing(1),
        backgroundColor: styles.cardHeaderBgColor,
        "& .MuiTypography-root": {
          color: styles.cardHeaderColor,
        },
      },
      ".dialogContent_root": {
        padding: theme.spacing(2),
        minHeight: 0,
      },
      ".buttonDelete": {
        backgroundColor: "#ec4139",
        color: "#fff",
        margin: "8px",
        "&:hover": {
          backgroundColor: "#ff3b31",
        },
      },
      ".dashboardCardTabs-root": {
        flexGrow: 1,
        width: "100%",
      },
      // side Nav Bar
      // side Nav Bar
      ".sidebarnav-list": {
        width: "100%",
        backgroundColor: theme.palette.background.paper,
        fontSize: 14,
        "& .MuiListItem-button": {
          paddingTop: 4,
          paddingBottom: 4,
        },
        "& .sidebarListItem": {
          "& p": {
            display: "inline-flex",
          },
          "& .MuiListItemIcon-root": {
            color: currentColors.primary,
          },
          "&.collapsed": {
            padding: 7,
            "& .MuiListItemIcon-root": {
              minWidth: "100%",
              marginRight: 50,
              justifyContent: "center",
            },
          },
          "&.expanded": {
            padding: "7px 15px",
            borderBottom: "1px solid #ebebeb",
            "& .MuiListItemIcon-root": {
              minWidth: 35,
            },
            "&:has(+.Mui-selected)": {
              border: "none",
              marginBottom: "1px",
            },
            "&:has(+.nested .subMenu .Mui-selected:nth-child(1))": {
              border: "none",
              marginBottom: "1px",
            },
            "&.Mui-selected": {
              border: "none",
              marginBottom: "1px",
            },
          },
        },
        "& .Mui-selected": {
          fontWeight: "bold",
          color: currentColors.primary,
        },
        "& .Mui-selected:hover": {
          backgroundColor: "#f5f5f5",
        },
        "& .Mui-selected .MuiListItemText-primary": {
          fontWeight: "bold",
        },

        "& .MuiList-root.subMenu": {
          fontSize: 13,
          "&.collapsed": {
            backgroundColor: "#f1f1f1",
            width: "100%",
            boxShadow: "inset 2px -1px 3px #00000010",
            borderRadius: "0 0 8px 8px",
            overflow: "clip",
          },
          "&.expanded": {
            paddingLeft: 10,
          },
        },

        "& div.sidebarListItem.collapsed:has(+div)": {
          backgroundColor: "#d0d0d0",
          borderRadius: "8px 8px 0 0",
          width: "100%",
          overflow: "clip",
        },
        "& a.sidebarListItem.collapsed": {
          marginBottom: 2,
        },
      },

      ".nested": {
        "& .MuiListItem-root": {
          paddingLeft: theme.spacing(2),
        },
      },
      ".tabPanel_content": {
        padding: theme.spacing(2),
        minHeight: 400,
      },
      ".cardView_container": {
        marginLeft: 0,
      },
      ".large": {
        width: theme.spacing(7),
        height: theme.spacing(7),
      },
      ".MuiCardActions-root": {
        backgroundColor: styles.cardHeaderBgColor,
      },
      ".MuiCardHeader-root": {
        backgroundColor: styles.cardHeaderBgColor,
        "&.dark": {
          backgroundColor: styles.cardHeaderBgDarkColor,
          "& .MuiCardHeader-content": { color: styles.cardHeaderDarkColor },
          "& .MuiCardHeader-title": { color: styles.cardHeaderDarkColor },
        },
      },
      ".MuiCardHeader-title": { fontSize: 18 },
      ".MuiCardHeader-action": { margin: 0 },
      ".MuiChip-root": {
        "& .MuiIcon-root": { color: styles.chipIconColor },
        "& .MuiChip-deleteIcon": { color: styles.chipIconColor },
      },
      ".MuiChip-clickable": {
        "&:hover": { backgroundColor: styles.chipBgColorActive },
        "&:focus": { backgroundColor: styles.chipBgColorActive },
      },
      ".toolbarTab_title ": {
        fontSize: 16,
      },
      ".cardView_Tours": {
        "& .icon_added": {
          position: "absolute",
          backgroundColor: "white",
          top: 0,
          left: 0,
          height: 24,
          width: 24,
          borderRadius: "50%",
          boxShadow: "0 3px 5px 2px rgba(0, 0, 0, .3)",
          textAlign: "center",
          color: "green",
          zIndex: 10,
        },
        "& .tour": {
          position: "relative",
          overflow: "visible",
          borderBottom: "1px solid #d2d2d2",
          marginBottom: theme.spacing(1.5),
          paddingBottom: theme.spacing(1),
          borderRadius: 0,
          "&:hover": {
            backgroundColor: "#f8fbfc",
          },
        },
        "& .no-tours-available": {
          minHeight: 100,
          "& .MuiAlert-icon": {
            fontSize: 27,
          },
        },
        "& .title_card_tour": {
          position: "relative",
          lineHeight: 1.2,
          fontSize: 18,
          "& .MuiChip-root": {
            marginLeft: theme.spacing(1.5),
            position: "absolute",
            right: 0,
            top: 0,
          },
        },

        "& .MuiListItem-root": {
          paddingTop: 0,
          paddingBottom: 0,
          alignItems: "start",
          marginBottom: 8,
        },
        "& .MuiListItemText-root": {
          marginTop: 0,
          marginBottom: 0,
        },
        "& .MuiListItemSecondaryAction-root": {
          top: "40%",
          right: 0,
        },
        "& .listItemTour": {
          "& .MuiAvatar-root": {
            width: 45,
            height: 45,
          },
          "& .MuiListItemText-primary": {
            color: styles.headingsColor,
            fontWeight: 600,
            fontSize: 13,
            letterSpacing: "normal",
            lineHeight: "1.2em",
            marginBottom: theme.spacing(0.5),
          },
          "& .MuiListItemText-secondary": {
            fontSize: 12,
            letterSpacing: "normal",
            lineHeight: 1.2,
          },
          "& .MuiListItemText-root ": {
            borderBottom: "1px solid #d2d2d2",
            paddingBottom: theme.spacing(1),
          },
          "& .btn-container": {
            marginTop: 0,
            marginRight: -16,
          },
        },
        "& .tabPanel_content": {
          padding: 0,
        },
        "& .col_labelsMobile": {
          display: "block",
          alignItems: "center",
          justifyContent: "center",
          [theme.breakpoints.up("md")]: {
            maxWidth: "25%",
            flexBasis: "25%",
          },
          "& .MuiChip-root": {
            marginLeft: 10,
          },
          "& span": { verticalAlign: "middle" },
          "& .MuiIcon-root": { fontSize: "1.2rem" },
        },
        "& .col_actions": {
          textAlign: "right",
          [theme.breakpoints.up("md")]: {
            maxWidth: 80,
            flexBasis: 80,
          },
        },
      },
      ".collapse_content_container": {
        paddingTop: theme.spacing(1.5),
        "& .title": {
          color: styles.headingsColor,
          fontWeight: 600,
        },
      },
      ".collapse_link": {
        padding: 0,
        textDecoration: "none",
        cursor: "pointer",
      },
      ".collapse": {
        "& .expand": {
          transform: "rotate(0deg)",
          marginLeft: "auto",
          transition: theme.transitions.create("transform", {
            duration: theme.transitions.duration.shortest,
          }),
        },
        "& .expandOpen": {
          transform: "rotate(180deg)",
        },
      },
      ".MuiCardContent-root": {
        paddingBottom: "16px !important",
      },
      ".MuiList-root": {
        "& .MuiListItem-container:last-child": {
          "& .MuiListItem-root": {
            marginBottom: 0,
          },
        },
      },
      ".accordion_paper": {
        padding: 0,
        marginBottom: theme.spacing(1.5),
      },
      ".chips": {
        "& .chip:hover": {
          backgroundColor: "#f7f4ff",
        },
        "& .chip.selected": {
          backgroundColor: "#f7f4ff",
        },
      },
      ".isDragging": {
        background: styles.draggableBgColor,
        borderLeft: "3px solid",
        borderColor: styles.draggableColor,
      },
      ".tableDragDrop": {
        "& .wrapper-add-button": {
          margin: 0,
        },
      },
      // ".filters": {
      //   display: "flex",
      //   justifyContent: "start",
      //   flexWrap: "wrap",
      //   listStyle: "none",
      //   padding: theme.spacing(0.5),
      //   margin: 0,
      //   borderRadius: theme.spacing(0.5),
      //   marginBottom: theme.spacing(2),
      //   "& .title_filters": {
      //     padding: theme.spacing(1),
      //     textTransform: "uppercase",
      //     fontSize: 14,
      //     lineHeight: "30px",
      //   },
      //   "& .title_filters_mobile": {
      //     marginRight: "30px",
      //     padding: theme.spacing(1),
      //     textTransform: "uppercase",
      //     fontSize: 12,
      //     lineHeight: "30px",
      //   },
      //   "& .autocomplete_container": {
      //     flexGrow: 1,
      //   },

      //   "& .button_filter": {
      //     padding: 4,
      //   },
      //   "& .MuiFormControl-root": {
      //     marginBottom: 0,
      //     paddingTop: 0,
      //   },
      //   "& .MuiFormLabel-root": {
      //     display: "none",
      //   },
      //   "& .MuiInputBase-root": {
      //     padding: theme.spacing(0.5),
      //   },
      // },
      ".list-item-autocomplete": {},
      ".list-items-cell": {
        padding: "0 4px 0 0",
        flexGrow: 0,
        overflow: "hidden",
        fontSize: 14,

        "& .wrapper-add-button": { width: 35, margin: 1 },
        "& .add-button": { width: 35, height: 35 },
        "& .text": { display: "block" },
      },

      ".list-items-icon": {
        padding: "0 0 0 0",
      },

      "#popover-advanced-search": {},

      ".autocomplete_custom_options": {
        "& .MuiAutocomplete-option": {
          borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
        },
      },

      ".label-type-tour": {
        backgroundColor: styles.chipBgColor,
        color: styles.chipColor,
      },

      ".label-counter": {
        position: "relative",
        paddingRight: 20,
        "& .MuiBadge-badge": {
          backgroundColor: styles.chipBgColor,
          color: styles.chipColor,
          right: 0,
          top: 13,
        },
      },
      ".my-tours-location": {
        marginBottom: theme.spacing(1),
        textTransform: "uppercase",
        fontSize: 12,
      },
      ".list-item-mytours": {
        marginBottom: theme.spacing(1),
        "& .btn-container": { marginRight: -16 },
      },
      ".listItemTour": {
        "& .MuiListItemText-primary": { margin: "0!important" },
      },
      ".btn-container": {
        marginRight: "-10px",
        marginLeft: "auto",
        display: "inline-block",
      },
      ".wrapper-add-button": {
        margin: theme.spacing(1),
        position: "relative",
        width: 35,
      },
      ".fabProgress": {
        color: currentColors.primary,
        position: "absolute",
        top: -2,
        left: -2,
        zIndex: 1,
      },
      ".loginProgress": {
        color: "#06C2C9",
        zIndex: 1,
      },

      ".wrapperButton": {
        position: "relative",
        display: "inline-block",
      },

      ".add-button": {
        boxShadow: styles.boxShadow,
        backgroundColor: styles.colors.accentColor,
        color: "white",
        "&:hover": { backgroundColor: styles.colors.accentColorHover },
      },
      ".remove-button": {
        border: "2px solid",
        borderColor: styles.colors.red,
        color: styles.colors.red,
        width: 35,
        height: 35,
      },
      ".tour-details-title": {
        marginBottom: theme.spacing(2),
      },
      ".card_mytours": {
        position: "sticky",
        top: 80,
        bottom: 20,
        zIndex: 5,
        "& .MuiCardContent-root": {
          maxHeight: "calc(100vh - 180px)",
          overflowY: "auto",
          overflowX: "hidden",
        },
      },
      ".empty-list": {
        textAlign: "center",
        padding: 20,
        "& span": {
          verticalAlign: "top",
        },
        "& .MuiAvatar-root": {
          margin: "auto",
          marginBottom: 16,
          "&.large": {
            width: 80,
            height: 80,
          },
        },

        "&.no-documents": {
          padding: 16,
        },
        "& .MuiTypography-body1": {
          fontWeight: "600",
          marginBottom: 8,
          fontSize: "12px",
        },
        "& .MuiButton-root": {
          marginTop: 20,
        },
      },
      ".closeButton": {
        position: "absolute",
        right: theme.spacing(0.5),
        top: theme.spacing(0.5),
        padding: 8,
      },
      ".closeDelete": {
        position: "absolute",
        right: theme.spacing(5.5),
        top: theme.spacing(0.5),
        padding: 8,
        color: styles.iconDeleted,
      },
      ".card_contracted": {
        "& .MuiCardHeader-root": {
          backgroundColor: "white",
          minHeight: 84,
        },
      },
      ".sticky-appbar": {
        position: "sticky",
        top: 0,
        bottom: 20,
        zIndex: 5,
        backgroundColor: styles.body.background,
        padding: "0 4px 0",
        marginLeft: -4,
        paddingTop: 20,
      },
      ".sticky-appbar-mobile": {
        position: "sticky",
        top: 0,
        bottom: 0,
        zIndex: 5,
        backgroundColor: styles.body.background,
        padding: "0 4px 0",
        marginLeft: -4,
        paddingTop: 0,
        marginBottom: 10,
      },
      ".tours-tabs-app-bar": {
        borderRadius: "4px 4px 0 0",
        backgroundColor: "white",
        overflow: "hidden",
        borderBottom: "1px solid #f7f4ff",
        "& .MuiTab-root": {
          padding: theme.spacing(4),
          paddingTop: theme.spacing(2),
          paddingBottom: theme.spacing(2),
          position: "relative",
          backgroundColor: "white",
          transition: styles.transitionButton,
          "& .MuiTypography-root": {
            textTransform: "none",
          },
          "& .fabProgress": {
            left: 8,
            top: 18,
          },
          "&.addingRemoving": {
            backgroundColor: "#f7f4ff",
            "& .label-counter .MuiBadge-badge": {
              backgroundColor: styles.colors.accentColor,
              color: "white",
            },
          },
          "&.addedRemoved": {
            backgroundColor: "inherit",
            "& .label-counter .MuiBadge-badge": {
              backgroundColor: styles.chipBgColor,
              color: styles.chipColor,
            },
          },
        },
      },
      ".tours-tabs": {
        overflow: "hidden",
        borderRadius: "0 0 12px 12px",
      },
      ".disabled": {
        backgroundColor: "#f0f0f0",
      },
      ".flat-card": { border: "none", boxShadow: "none" },
      ".cities-chips": { marginBottom: 16, textAlign: "left", width: "100%" },
      ".city-chip": {
        marginRight: 8,
        marginBottom: 4,
        backgroundColor: "#f7f4ff",
        "& .MuiIcon-root": { color: currentColors.primary },
      },
      ".attractions-chips": { marginBottom: 16 },
      ".attraction-chip": { marginRight: 8, marginBottom: 4 },
      ".notes": {
        marginTop: 12,
      },
      ".vertical-tabs": {
        flexGrow: 1,

        display: "flex",
        height: 224,
        "& .tabs": {
          borderRight: `1px solid ${theme.palette.divider}`,
          width: "100%",
          maxWidth: 150,
        },
        "& .MuiTab-root": {
          padding: 0,
        },
        "& .MuiTab-wrapper": {
          alignItems: "start",
          fontSize: 12,
          position: "relative",
        },
        "& .tab-panel-notes": {
          padding: 12,
        },
        "& .title": {
          marginBottom: 12,
        },
        "& .title-note": {
          marginBottom: 12,
        },
      },
      ".avatar-profile": {
        width: 80,
        height: 80,
        cursor: "pointer",
        margin: "auto",
        backgroundColor: "#e7eaff",
        color: currentColors.primary,
        marginBottom: 12,
        "&:hover": {
          boxShadow: styles.boxShadowButtonHover,
        },
        "& .MuiIcon-root": {
          fontSize: 30,
        },
      },
      ".button-upload": {
        backgroundColor: "#F2F5FC",
        color: "#6a77c5",
        boxShadow: "none",
        padding: "8px 12px",
        fontSize: 12,
        textTransform: "uppercase",
        margin: "auto",
        "&:hover": {
          backgroundColor: "#e7eaff",
          boxShadow: styles.boxShadowButtonHover,
        },
      },
      ".TabPanel.Tours": {
        marginTop: "0!important",
      },
      ".dialog-upload": {
        "& .MuiDropzoneArea-text": {
          fontSize: 20,
        },

        "& .MuiDialog-paperWidthSm": {
          width: "100%",
        },

        "& .MuiTypography-subtitle1": {
          marginBottom: 8,
        },

        "& .MuiDialogContent-root": {
          overflowX: "hidden",
        },

        "& .MuiDropzonePreviewList-imageContainer": {
          padding: "16px !important",
          width: "fit-content !important",
          maxWidth: "fit-content",
          flexBasis: "auto",
        },

        "& .MuiGrid-spacing-xs-8": {
          width: "100%",
          margin: 0,
        },

        "& .MuiDropzoneArea-root": {
          minHeight: 0,
          height: "fit-content",
          marginBottom: 12,
          paddingBottom: 12,
        },

        "& .custom-overlay__caption": {
          backgroundColor: "rgba(0, 0, 0, 0.6)",
          position: "absolute",
          bottom: "0",
          width: "100%",
          color: "white",
          padding: "2px",
          fontSize: "90%",
          overflowWrap: "anywhere",
        },

        "& .filterGalleryField": {
          paddingTop: 0,
        },

        "& .pointer": {
          cursor: "pointer",
        },
      },

      ".dialog-details-list": {
        "& #dialog-upload-title": {
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        },

        "& .MuiDropzoneArea-text": {
          fontSize: 20,
        },

        "& .MuiDialog-paperWidthSm": {
          width: "100%",
          maxWidth: "90%",
          minHeight: "40%",
        },
        "& .MuiDialog-paperWidthMd": {
          width: "100%",
          maxWidth: "1000px",
          minHeight: "40%",
        },

        "& .MuiTypography-subtitle1": {
          marginBottom: 8,
        },

        "& .MuiDialogContent-root": {
          overflowX: "hidden",
        },
      },

      ".documents_table_row": {
        "& .MuiTableCell-root": {
          padding: "16px 12px",
          "&.cell_name_icon": {
            paddingLeft: "100px!important",
            position: "relative",
          },
        },

        "& .name_icon": {
          position: "absolute",
          left: 8,
          top: 0,
          bottom: 0,
          margin: "auto",
          height: 36,
        },
      },
      ".label_title": {
        color: styles.inputsLabelColor,
        fontSize: styles.inputsLabelFontSize,
        fontWeight: styles.inputsLabelFontWeight,
        lineHeight: 1.2,
        fontFamily: styles.body.fontFamily,
        marginBottom: 16,
      },
      ".label_title_checkbox": {
        color: styles.inputsLabelColor,
        fontSize: styles.inputsLabelFontSize,
        fontWeight: styles.inputsLabelFontWeight,
        lineHeight: 1.2,
        fontFamily: styles.body.fontFamily,
      },
      ".selectable_chips": {
        "& .selectable_chips_container": { display: "flex" },

        "& .chips": {
          marginBottom: 16,
          "& .chip": {
            marginRight: 8,
            marginBottom: 8,
          },
        },
        "& .MuiChip-root .MuiIcon-root": { color: styles.chipIconColorActive },
        "& .button_filter": {
          width: "22%",
        },
        "& .autocomplete_container": {
          display: "flex",
          alignItems: "center",
          "& .MuiAutocomplete-root": {
            width: "30%",
            marginRight: 8,
          },
          "& .MuiButton-root": {
            maxHeight: 36,
          },
          "& .button_filter": {
            width: "30%",
            marginTop: 8,
          },
        },
      },
      ".MuiTypography-root.leyend-form": {
        fontSize: 18,
        fontFamily: styles.fontsHeadings,
      },
      ".tabsDestinationsDetail": {
        "& .MuiTab-root": {
          minWidth: 130,
        },
      },
      ".actions-buttons": {
        marginTop: 16,
        "& .MuiButton-root": { marginRight: 12 },
      },
      ".caption-table": {
        fontSize: 14,
        fontFamily: styles.fontsHeadings,
      },
      ".label-summary": {
        backgroundColor: styles.chipBgColor,
        color: styles.chipColor,
        textAlign: "center",
        borderRadius: 16,
        display: "inline-block",
        padding: "4px 12px",
        fontSize: 12,
        overflow: "hidden",
        whiteSpace: "nowrap",
        textOverflow: "ellipsis",
        border: "1px solid",
        borderColor: styles.chipColor,
        marginRight: 16,
      },

      ".label-cancelation": {
        display: "block",
        fontSize: "12px",
        marginTop: "5px",

        color: "#666",
        width: "100%",
      },

      ".tittle-summary": {
        marginTop: "-8px",
      },

      ".label-tag": {
        backgroundColor: styles.chipBgColor,
        color: styles.chipColor,
        textAlign: "center",
        borderRadius: 16,
        display: "inline-block",
        padding: "4px 12px",
        fontSize: 12,
        maxWidth: 120,
        overflow: "hidden",
        whiteSpace: "nowrap",
        textOverflow: "ellipsis",

        "&.statusPublished": {
          backgroundColor: styles.tagColorPublished,
          color: "white",
        },
        "&.statusPUBLISHED": {
          backgroundColor: styles.tagColorPublished,
          color: "white",
        },
        "&.statusGUIDECONFIRMATION": {
          backgroundColor: styles.tagColorPublished,
          color: "white",
        },
        "&.statusCUSTOMERCONFIRMATION": {
          backgroundColor: styles.tagColorPublished,
          color: "white",
        },
        "&.statusACTIVE": {
          backgroundColor: styles.tagColorPublished,
          color: "white",
        },
        "&.statusDRAFT": {
          backgroundColor: styles.tagColor,
        },
        "&.review": {
          backgroundColor: "rgb(255 165 0 / 40%)",
          color: "black",
        },

        "&.statusAPPROVED": {
          backgroundColor: styles.tagColorPublished,
          color: "white",
        },
        "&.statusCONFIRMED": {
          backgroundColor: styles.tagColorPublished,
          color: "white",
        },

        "&.statusOTHER": {
          backgroundColor: styles.tagColor,
        },
        "&.statusPENDING": {
          backgroundColor: "#ffeaea",
          color: "#ff4646",
        },
        "&.statusCANCELLED": {
          backgroundColor: "#ffeaea",
          color: "#ff4646",
        },
      },
      ".breadcrumbs_destinations_list": {
        "& .MuiBreadcrumbs-separator": {
          opacity: ".4",
        },
        "& .MuiBreadcrumbs-li": {
          "& .MuiLink-root": {
            opacity: ".4",
            minWidth: 80,
            fontSize: 12,
            display: "inline-block",
            "&:hover": {
              opacity: ".8",
              color: styles.linkColorHover,
            },
          },
          "&:last-child": {
            "& .MuiLink-root": {
              opacity: "1",
              fontSize: 15,
              "&:hover": {
                opacity: "1",
              },
            },
          },
          "&.MuiBreadcrumbs-separator:last-child": {
            color: styles.linkColorHover,
          },
        },
      },
      ".table-collapsible": {
        border: "none",
        backgroundColor: "#fbfbfb",
        width: "100%",
      },
      ".popover": {
        pointerEvents: "none",
        "& .paper": {
          padding: theme.spacing(1),
          maxWidth: 250,
        },
        "& .popover-text": {
          fontSize: 12,
        },
      },
      ".tabsAttractionsDetail": {
        "& .MuiTab-root": {
          minWidth: 125,
        },
      },
      ".tabsDetailMobile": {
        "& .MuiTab-root": {
          minWidth: 0,
        },
      },
      ".verified": {
        color: styles.colors.secondary,
      },
      ".table-collapse": {
        "& > *": {
          borderBottom: "unset",
        },
        "& .MuiTypography-root": {
          fontFamily: "inherit",
        },
      },
      ".MuiTab-textColorInherit": {
        opacity: 0.9,
      },
      ".label-price ": {
        padding: "4px 8px",
        backgroundColor: "#e5e8e9",
        borderRadius: 8,
      },
      ".btns_cart": {
        marginTop: 22,
      },
      ".btn_search": {
        height: 40,
        marginTop: 21,
      },
      ".TextFieldTextArea": {
        fontSize: "5px",
      },
      ".selectRole": {
        minwidth: "200px",

        "MuiListItem-gutters": {
          height: 10,
        },
      },

      // Overwrite Material Select
      ".MaterialSelect": {
        borderRadius: 8,
        height: 44,
        marginTop: 5,
        "&.MuiOutlinedInput-root": {
          borderRadius: 8,
        },
        "& .MuiSelect-select": {
          "& .title": {
            color: "#1E1E1E",
            fontWeight: "normal",
          },
        },
        "&.isPlaceholder .MuiSelect-select": {
          color: "#b1b1b1",
          fontSize: 13,
        },
        "& em": {
          color: "#b1b1b1",
          fontStyle: "normal",
        },
        "& .MuiSvgIcon-root": {
          marginRight: "8px",
          pointerEvents: "none",
          position: "absolute",
          right: 0,
        },
      },
      // Overwrite Material TextField
      ".MaterialTextField": {
        "&.MuiFormControl-root": {
          marginTop: 5,
        },
        "& .MuiFormHelperText-root": {
          color: "#d53737",
        },
        "& .MuiInputBase-root": {
          paddingRight: "14px !important",
        },
        "&.MuiTextField-root": {
          borderRadius: 8,
          borderColor: "#757575",
          "& .MuiOutlinedInput-root": {
            borderColor: "#757575",
            borderRadius: 8,
            height: 44,
            "&.Mui-disabled": {
              backgroundColor: "#f5f5f5",
              border: "1px solid #d6d6d6",
            },
          },
        },
      },

      ".customTextField": {
        position: "relative",
        width: "100%",

        "& .label": {
          color: styles.inputsLabelColor,
          fontSize: styles.inputsLabelFontSize,
          fontWeight: styles.inputsLabelFontWeight,
          width: "100%",
          lineHeight: 1.2,
          fontFamily: styles.body.fontFamily,
          top: 0,
          left: 0,
          position: "absolute",
        },
        "& .input": {
          padding: "0 8px",
          border: "1px solid ",
          width: "100%",
          backgroundColor: styles.inputBgColor,
          color: styles.inputColor,
          borderColor: "#135265",
          borderRadius: 4,
          height: 40,
          overflow: "visible",
          "&:hover": { borderColor: "#7c87c7" },
          "& p": { marginTop: 8 },
        },
      },
      ".popover-cart": {
        width: "300px",
        fontSize: 12,
      },
      ".popover-ages": {
        width: "220px",
      },
      ".btn-popover-ages": {
        position: "absolute",
        right: 0,
        bottom: 6,
      },
      ".scheduleInput-container": {
        "& .MuiFormControl-root": { marginBottom: 0 },
        "& .SelectSmall": {
          "& .MuiSelect-selectMenu": {
            paddingTop: 9.5,
            paddingBottom: 8.5,
          },
        },
      },
      ".search-summary": {
        fontSize: 12,
        "& span": {
          marginRight: 8,
          display: "inline-block",
        },
      },
      ".summary-cart": {
        width: "auto",
        position: "absolute",
        right: 0,
        top: 0,
        paddingTop: 0,
        marginTop: "-4px",
        cursor: "pointer",
        display: "inline-block",
        "&.cardComponentSearch": {
          top: -54,
        },

        "& .input": {
          position: "relative",
          paddingLeft: 40,
        },
        "& .text": { paddingRight: 40 },
        "& .icon": {
          position: "absolute",
          left: 5,
          top: 7,
        },
        "& .btn-popover-summary-cart": {
          position: "absolute",
          top: 3,
          right: 3,
        },
      },
      ".break-line-grid": {
        marginBottom: "-14px",
        marginTop: "-14px",
      },
      ".title-group": {
        color: styles.headingsColor,
        fontWeight: "bold",
      },
      ".datepicker": {
        width: "100%",
      },
      a: {
        color: styles.linkColor,
      },
      ".select-quantity": {
        paddingTop: 0,
        marginBottom: 0,
      },

      ".inline-input": {
        position: "relative",
        display: "flex",
        alignItems: "center",

        "& .label": {
          color: styles.inputsLabelColor,
          fontSize: styles.inputsLabelFontSize,
          fontWeight: styles.inputsLabelFontWeight,
          fontFamily: styles.body.fontFamily,
        },
        "& .input": {
          display: "inline-block",
          marginLeft: 10,
          width: 140,
        },
        "& .MuiFormControl-root": {
          paddingTop: 0,
          marginBottom: 0,
        },
      },
      ".rate-item": {
        padding: "8px 12px",
        backgroundColor: styles.cardBgColor,
        marginBottom: 8,

        "& .MuiTypography-body2": {
          fontSize: 13,
        },
      },
      ".finance-item": {
        padding: "8px 12px",
        backgroundColor: "#f9fbfb",
        marginBottom: 8,

        "& .MuiTypography-body2": {
          fontSize: 13,
        },
      },
      ".btn-book-ticket": {
        height: 40,
      },
      ".total-price": {
        "& .price": { fontSize: 20 },
      },
      ".preload-modal": {
        textAlign: "center",
        "& .fabProgress": {
          margin: "20px auto",
          right: 0,
          position: "relative",
        },
      },
      ".detail_dashboard.TicketBookingProcess": {
        height: "calc(100vh - 64px)",
        position: "relative",
        overflow: "hidden",
        "& .content_detail_dashboard": {
          display: "flex",
          flexFlow: "row wrap",
          flexDirection: "column",
          paddingBottom: 0,
        },
        "& .top": {
          width: "100%",
        },
        "& .content-flex": {
          flex: 1,
          width: "100%",
          flexGrow: 1,
        },
        "& .cardTableContainer": {
          marginBottom: 0,
        },
      },
      ".flex-vh-container": {
        height: "80vh",
        position: "relative",
      },
      ".flex-vh-container-inner": {
        display: "flex",
        flexFlow: "row wrap",
        flexDirection: "column",
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
      },

      ".flex-vh-container-inner-top": {
        width: "100%",
      },
      ".flex-vh-container-inner-content-flex": {
        flex: 1,
        width: "100%",
      },
      ".table-sticky-container": {
        height: "100%",
        position: "relative",
      },
      ".table-sticky-header": {
        display: "flex",
        flexFlow: "row wrap",
        flexDirection: "column",
      },
      ".table-head-sticky": {
        height: 48,
        paddingRight: 20,
        backgroundColor: "#fafafa",
      },
      ".table-body-sticky": {
        flex: 1,
        overflowY: "auto",
        overflowX: "hidden",
        paddingBottom: 100,
      },
      ".list-item-autocomplete-booking-search": {
        "& .text": { fontSize: 13 },
      },
      ".btn-handleCollapse": {
        padding: 0,
        fontSize: 13,
        marginLeft: 10,
        textTransform: "uppercase",
        position: "relative",
        display: "inline-block",
        "&.show-less": {
          paddingLeft: 20,
          "& .MuiIcon-root": {
            left: -2,
          },
        },
        "& .MuiIcon-root": {
          position: "absolute",
          top: -2,
          fontSize: 20,
        },
      },
      ".link": {
        color: styles.linkColor,
        cursor: "pointer",
      },
      ".header-ticket-types": {
        padding: "0 0 8px 0",
        display: "flex",
      },
      ".selectSchelude .MuiSelect-selectMenu": {
        paddingTop: 7.5,
        paddingBottom: 7.5,
        fontSize: 14,
      },
      ".FilterTableListBookingSearch": {
        "& .MuiFormControl-root": { marginBottom: 0 },
      },
      ".form-control": {
        textAlign: "left",
        paddingTop: 29,
        position: "relative",
        display: "inline-flex",
        flexDirection: "column",
        verticalAlign: "top",
        width: "100%",
        "& .input-label": {
          position: "absolute",
          top: 0,
          left: 0,
          color: styles.inputsLabelColor,
          transform: "none",
          whiteSpace: "nowrap",
          fontSize: styles.inputsLabelFontSize,
          fontWeight: styles.inputsLabelFontWeight,
          textOverflow: "ellipsis",
          overflow: "hidden",
          width: "100%",
          lineHeight: 1.2,
          fontFamily: styles.body.fontFamily,
        },
      },
      ".iconTrueCont": { position: "relative" },
      ".iconTrue": { color: styles.chipColor, fontSize: 18 },
      ".iconFalse": { color: styles.tagColor, fontSize: 18 },

      ".iconAlert": {
        color: styles.colors.red,
        fontSize: 22,
        position: "relative",
        top: 8,
      },
      ".link-button:hover": {
        textDecoration: "none",
      },

      ".btonNeedsReview": {
        padding: "5px 15px 5px 15px",
      },
      ".btonSmall": {
        padding: "2px 3px 3px 2px",
        fontSize: "11px",
        width: "80px",
      },

      ".iconNeedsReview": {
        color: styles.chipColor,
        fontSize: 22,
      },

      ".fontSmall": {
        fontSize: 13,
      },

      ".days-of-week": {
        marginBottom: 8,

        "& .MuiChip-root": {
          marginRight: 8,
          padding: 4,
        },
        "& .MuiChip-label": { textTransform: "uppercase", fontSize: 16 },
      },

      ".pickers": {
        display: "inline-flex",
        marginTop: 3,

        "& .datepicker": {
          display: "inline-flex",
          width: "inherit",
          marginRight: 8,
          paddingTop: 0,
          marginBottom: 0,
          maxWidth: 235,
        },
      },
      ".cardView_Tour": {
        "& .MuiGrid-container": {
          justifyContent: "space-between",
          alignItems: "center",
        },
        "& .MuiIconButton-root": {
          padding: "0 10px 0 13px",
        },
      },
      ".dateFromTimesItem": {
        position: "relative",
        "& .iconDelete": {
          position: "absolute",
          right: 5,
          top: "25px",
          bottom: 0,
          width: 40,
          height: 40,
          margin: "auto",
        },
      },

      ".tab-panel-availability": {
        "& .MuiCardContent-root": {
          position: "relative",

          "& .availabilityList-item": {
            width: "100%",
          },
        },
      },

      ".availabilityList-item": {
        borderLeft: "2px solid",
        borderColor: styles.colors.secondary,
        borderRadius: "12px !important",
        marginBottom: "16px",

        "& .MuiAccordionSummary-root": {
          minHeight: 61,
        },

        "& .datepicker": {
          padding: 0,
          margin: "0 20px 0 0",
        },

        "& .actionButtons": {
          padding: "11px 0 10px 0",
        },
      },

      ".copyTimeModal": {
        "& .MuiChip-root": {
          margin: "4px 8px 4px 0",
          padding: 4,
          width: 95,
        },

        "& .MuiChip-label": {
          textTransform: "uppercase",
          fontSize: 16,
        },
      },

      ".cancelBookingModal": {
        "& .MuiDialogTitle-root": {
          display: "flex",
          flexDirection: "column",

          "& .title": {
            color: "#000",
            display: "flex",
            alignItems: "center",

            "& .MuiSvgIcon-root": {
              color: "#d32f2f",
              marginRight: 5,
            },
          },

          "& .subtitle": {
            fontSize: "0.9rem",
          },
        },

        "& .MuiDialogContent-root .label .optionalText": {
          fontSize: "0.9rem",
          color: "#a5a5a5",
        },
      },

      ".refundBookingModal": {
        "& .MuiDialogTitle-root": {
          display: "flex",
          flexDirection: "column",

          "& .title": {
            color: "#000",
            display: "flex",
            alignItems: "center",
          },

          "& .subtitle": {
            fontSize: "0.9rem",
          },
        },

        "& .MuiDialogContent-root": {
          "& .MuiButtonBase-root": {
            borderRadius: 8,
          },

          "& .originalAmountContainer": {
            padding: 20,
            fontWeight: 500,
            backgroundColor: "#f1f4f5",
            borderRadius: 8,
            display: "flex",
            justifyContent: "space-between",

            "& .amount": {
              color: "#000",
              fontWeight: "bold",
              fontSize: 18,
            },
          },

          "& .refundMethodsContainer": {
            display: "flex",
            gap: 15,
            marginTop: 10,

            "& .MuiButton-root": {
              flex: 1,
              flexDirection: "column",
              padding: 15,
              fontSize: 16,
              borderWidth: 2,
              borderColor: "#f1f4f5",

              "&.selected": {
                borderColor: "#135265",
                backgroundColor: "#1352651a",
              },
            },
          },

          "& .fixedAmountContainer": {
            display: "flex",
            marginTop: 10,

            "& .inputContainer": {
              flex: 1,
            },

            "& .maxButton": {
              height: 44,
              marginLeft: 10,
            },
          },

          "& .percentageContainer": {
            marginTop: 10,

            "& .percentageButtonsContainer": {
              display: "flex",
              gap: 10,
              marginTop: 10,

              "& .MuiButton-root": {
                flex: 1,
              },
            },
          },

          "& .resumeContainer": {
            marginTop: 20,
            padding: "15px 20px",
            background: "#edfff4",
            borderRadius: 8,
            border: "1px solid #a6f8c6",
            display: "grid",
            gap: 10,

            "& .refundAmount, & .remainingAmount": {
              display: "flex",
              justifyContent: "space-between",
              fontWeight: 500,
              lineHeight: "30px",

              "& .amount": {
                color: "#000",
                fontWeight: "bold",
              },
            },

            "& .refundAmount .amount": {
              fontSize: 20,
            },

            "& .percentAmount": {
              fontSize: 14,
            },
          },
        },
      },

      ".discardChangesAccountModal": {
        "& .MuiDialogTitle-root": {
          display: "flex",
          alignItems: "center",
          color: "#000",
          fontSize: 20,
          fontWeight: "bold",

          "& .MuiSvgIcon-root": {
            color: "rgb(237, 108, 2)",
            marginRight: 10,
          },
        },

        "& .MuiDialogContent-root": {
          paddingLeft: 64,

          "& .MuiTypography-root": {
            fontSize: 15,
          },
        },
      },

      ".availabilityList-item:last-child": {
        marginBottom: "4px !important",
      },

      ".availabilityList-item.MuiAccordion-root:before": {
        backgroundColor: "transparent",
      },

      ".hidden-tab": { display: "none" },

      ".iconToolip": {
        color: styles.chipColor,
        fontSize: 15,
      },
      ".MuiOutlinedInput-input.Mui-disabled": {
        textOverflow: "ellipsis",
      },

      ".PrivateNotchedOutline": {
        backgroundColor: "white",
        border: "1px solid",
        borderColor: styles.inputBorderColor,
        color: styles.inputColor,
      },

      ".multipleSelectContainer": {
        position: "relative",
      },
      ".multipleSelectInput": {
        position: "absolute",
        top: -40,
      },

      ".MuiAutocomplete-input": {
        "&::placeholder": {
          color: "#154153!important",
          fontSize: "16px!important",
          fontStyle: "normal!important",
        },
      },
      ".autocomplete-input": {
        "& .MuiInputBase-root": {
          padding: "0px 0px 0px 6px !important",

          "& .MuiInputBase-input": {
            width: 0,
            minWidth: 30,
            flexGrow: 1,
          },
        },
      },
      ".formControlLabelText": {
        fontSize: "12px",
      },
      ".MuiButtonBase-root.buttonGeneralStyles": {
        textTransform: "none !important",
        borderRadius: "8px !important",
        padding: "7px 30px",
        fontSize: 14,
        whiteSpace: "nowrap",
      },
      ".MuiButton-containedSizeLarge": {
        padding: "3px 21px",
        fontSize: "1rem",
      },
      ".MuiButton-outlinedSizeLarge": {
        padding: "4px 12px",
        fontSize: "1rem",
      },

      // Login page - Container
      ".loginPageContainer": {
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        "& .buttons-container": {
          display: "flex",
          justifyContent: "flex-end",
          gap: "8px",
          width: "100%",
        },
        "& .forgot-password": {
          justifyContent: "flex-end",
        },
        "& .loginTitles": {
          marginBottom: 40,
        },
        "& .form": {
          width: "600px",
          "& .title": {
            fontSize: "24px",
          },
          "& .subtitle": {
            fontSize: "16px",
            color: "#757575",
          },
        },
      },
      // Filter Container - List view
      ".viewListFilterContainer": {
        padding: "5px",
        // display: 'flex',
        justifyContent: "space-between",
        alignItems: "center",
        "& .flexContainerCenter": {
          display: "flex",
          alignItems: "center",
          justifyContent: "right",
        },
        "& .inputsContainer": {
          "& .filterStackComponent": {
            alignItems: "flex-start",
          },
          "& .MuiInputLabel-root": {
            position: "relative",
            top: "11px !important",
            left: "-10px",
            fontSize: "16px",
            fontWeight: 600,
          },
        },
        "& .MuiFormLabel-root, & .MuiFormLabel-root": {
          color: "#757575 !important",
        },
        "& .filterInputsContainer": {
          flexGrow: 1,
          "& .MuiGrid-root.MuiGrid-item": {
            paddingTop: 7,
            paddingLeft: 12,
          },
          "& .MuiGrid-container": {
            width: "calc(100% + 12px)",
            marginLeft: "-12px",
          },
        },
        "& .buttonsContainer": {
          marginTop: 18,
          marginLeft: 12,
          width: "88px" /* Ancho fijo */,
          display: "flex",
          justifyContent: "flex-start",
          "& .button": {
            width: 40,
            height: 40,
            minWidth: 40,
            padding: 0,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            "& .MuiButton-startIcon": {
              margin: 0,
            },
          },
        },
        "& .actionsButtonsContainer": {
          alignItems: "center",
          justifyContent: "space-between",
        },
      },
      // Common Components - Input Toogle Button Rounded
      ".toogleButtonRounded": {
        position: "relative",
        zIndex: 10,
        margin: "5px 0px 5px 15px",
        borderRadius: "25px !important",
        boxShadow:
          "rgba(0, 0, 0, 0.2) 0px 4px 3px 0px inset, rgba(0, 0, 0, 0.14) 0px 3px 2px -1px inset",
        outline: "2px solid #135265",
        outlineOffset: "-1px",
        height: "26px",
        overflow: "hidden",
        backgroundColor: "#FFFFFF",
        "& .button": {
          borderRadius: "50%",
          color: "#135265",
          position: "relative",
          border: "none",
          width: "26px",
          "&.MuiToggleButtonGroup-lastButton, &.MuiToggleButtonGroup-middleButton":
            {
              marginLeft: "2px",
            },
          "& span.material-icons": {
            //lineHeight: "1.05em",
          },
          "& .MuiToggleButton-root": {
            borderRadius: "50%",
          },
          "&.Mui-selected	": {
            borderRadius: "50%",
            backgroundColor: "#135265",
            color: "white",
            zIndex: 20,
            pointerEvents: "none",
            boxShadow:
              "rgba(0, 0, 0, 0.2) 0px 4px 3px 0px, rgba(0, 0, 0, 0.14) 0px 3px 2px -1px",
          },
        },
      },
      // Table List Container - List view
      ".mainAdminContainer": {
        backgroundColor: "#f5fafe",
        padding: "15px !important",
      },
      // Pagination Table container
      ".paginationTableContainer": {
        padding: "10px 15px",
        backgroundColor: "#F8F8F8",
        fontSize: 12,
        "& .select": {
          borderRadius: 8,
          marginLeft: 5,
          marginRight: 5,
          backgroundColor: "white",
          "& .MuiSelect-select": {
            padding: "4px 15px",
          },
        },
        "& .MuiPaginationItem-root": {
          "&.MuiPaginationItem": {
            color: "#135265",
            backgroundColor: "#135265",
          },
        },
      },
      // Sidebar Menu
      ".sidebarMenuContainer": {
        padding: "5px !important",
        "&.sidebarMenuContainer": {
          padding: "5px 5px 5px 4px !important",
        },
        "& .nested": {
          "& .MuiListItem-root.collapsed": {
            borderRadius: 0,
            "&:hover": {
              borderRadius: 0,
            },
          },
        },
        "& .MuiListItem-root": {
          color: "#135265",
          "&.Mui-selected	": {
            backgroundColor: "#cae4f9",
            borderRadius: 10,
          },
          "&.expanded:has(+.MuiListItem-root:hover)": {
            borderBottom: "none",
            marginBottom: 1,
          },
          "&.expanded:hover": {
            borderBottom: "none",
            marginBottom: 1,
          },
          "&:hover": {
            borderRadius: 10,
          },
        },
      },
      // Component - Create Schedules Pricing
      // Component - Create Schedules Pricing
      // Component - Create Schedules Pricing
      ".componentCreateSchedulesPricing": {
        // fila de dynamic pricing (todas las filas, incluida la newRuleRow)
        "& .itemsCreatedToEditDelete .itemCreated.formatTable": {
          display: "grid",                     // en vez de flex
          gridTemplateColumns: "40px 1fr 1fr auto", // Rule / Sold / Add / Actions
          alignItems: "center",
          columnGap: 12,

          "& .columnDescription": {
            width: "200px",
            textAlign: "left",
          },
          "& .column": {
            width: "auto",
            textAlign: "center",
          },
          "& .columnActions": {
            width: "auto",
            textAlign: "right",
          },
        },
      },
      // Component - Number Increase
      ".componentNumberIncrease": {
        width: "100%",
        marginTop: 5,
        marginBottom: 10,
        "& .labelTop": {
          fontSize: 14,
          color: styles.inputs.colorLabel,
          fontWeight: 500,
          paddingBottom: 5,
        },
        "& .outlinedInputNumberIncrease": {
          "& input[type=number]": {
            MozAppearance: "textfield",
            "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
              WebkitAppearance: "none",
              margin: 0,
            },
          },
          borderRadius: "12px",
          height: 44,
        },
      },
      // Component - Alert feedback
      ".componentAlertFeedback": {
        borderRadius: "8px !important",
        marginBottom: 15,
        fontSize: 14,
        "&.cancelAlert": {
          fontWeight: 500,
          "&.MuiAlert-colorError .MuiAlert-message": {
            color: "#d32f2f",
          },
          "&.MuiAlert-colorSuccess .MuiAlert-message": {
            color: "#2e7d32",
          },
        },
        "&.withoutMarginBottom": {
          marginBottom: 0,
        },
        "& .MuiButtonBase-root.MuiButton-root.selected": {
          backgroundColor: currentColors.primaryButtonColorSelected,
        },
      },
      // Component - Toogle Buttons
      ".componentButtonGroupToogle": {
        "& .MuiButtonBase-root.MuiButton-root.selected": {
          backgroundColor: currentColors.primaryButtonColorSelected,
        },
      },
      // container - GRID Line with multiple inputs types
      ".containerGridMultipleInputsLine": {
        display: "flex",
        "& .gridText": {
          fontSize: "14px",
          color: styles.colors.darktGrey,
          fontWeight: 500,
        },
        "& .gridTitle": {
          fontSize: "14px",
          color: styles.colors.lightGrey,
          fontWeight: 500,
        },
        "& .gridHeader": {
          alignItems: "center",
          padding: 5,
        },
        "& .MuiGrid-container": {
          alignItems: "center",
          padding: 5,
        },
        "& .MuiGrid-item": {
          alignItems: "center",
          // padding: "5px 10px",
          // paddingTop: 5,
          // paddingLeft: 5,
          // paddingBottom: 5,
          // paddingRight: 5,
        },
        "& .gridRow.MuiGrid-item": {
          // alignItems: "center",
          // padding: "5px 10px",
        },
      },
      // Component - Basic Date Picker
      ".componentBasicDatePicker": {
        "& .MuiInputBase-root.MuiOutlinedInput-root": {
          borderColor: "#757575",
          borderRadius: 8,
          height: 44,
        },
      },
      // Table List Container - List view
      ".viewListGeneralContainer": {
        paddingLeft: 10,
        paddingRight: 10,
        backgroundColor: "white",
      },
      // Component - Tabs with body
      ".componentDraggableSortListItems": {
        // border: '1px solid #B3B3B3',
        border: "1px solid #B3B3B3",
        borderRadius: 8,
        padding: 15,
        "& .dragIcon": {
          backgroundColor: "#15526421",
          borderRadius: 5,
          height: 25,
          paddingTop: 3,
          color: "#155264",
        },
        "& .tableListContainer": {
          "& tr": {
            borderBottom: "2px solid #c5c5c5 !important",
          },
          "& td": {
            borderLeft: "1px solid #dfdfdf !important",
            borderRight: "1px solid #dfdfdf !important",
            height: 50,
          },
          "& .MuiOutlinedInput-root": {
            height: 35,
          },
        },
        // "& .buttonGeneralStyles": {
        //   border: '1px solid orange',
        // },
      },
      // Component - Tabs with body
      ".tabsModuleSectionsContainer": {
        padding: "10px 10px 10px 0px",
        position: "relative",
        zIndex: 100,
        "& .sectionComponent": {
          marginBottom: "0 !important",
        },
        "& .tabsNavigationComtainer": {
          position: "relative",
          zIndex: 100,
          "& .MuiTabs-flexContainer": {
            paddingLeft: 6,
          },
        },
        "& .tabsBodyComtainer": {
          boxShadow: "-1px 2px 4px 1px rgb(0 0 0 / 12%)",
          borderRadius: "0px 16px 16px 16px",
          padding: "5px 10px 10px 10px",
          marginLeft: 6,
          backgroundColor: "#FFFFFF",
          "& >div": {
            "& >.MuiBox-root": {
              padding: 10,
            },
          },
        },
        "& .MuiTabs-root": {
          "& .MuiButtonBase-root": {
            backgroundColor: "#F8F8F8",
            // border: '1px solid #d3d3d3',
            borderRadius: "8px 8px 0px 0px !important",
            marginRight: 3,
            color: "#1C1C1C",
            fontSize: 16,
            textTransform: "none",
            minWidth: 150,
            position: "relative",
            boxShadow: "0px 7px 4px 1px rgb(0 0 0 / 12%)",
            bottom: "-5px",
            paddingBottom: "16px",
            "&.Mui-selected": {
              boxShadow: "0px 2px 4px 1px rgb(0 0 0 / 12%)",
              borderBottom: "none",
              borderRadius: "8px 8px 0px 0px",
              backgroundColor: "#FFF",
              marginRight: 3,
              marginTop: 3,
              color: "#1C1C1C",
              position: "relative",
              zIndex: 200,
              bottom: "0px",
              paddingBottom: "12px",
            },
            "& .MuiBadge-root": {
              marginLeft: 18,
              "& .MuiBadge-badge": {
                fontSize: "13px",
                borderRadius: 8,
                padding: 10,
              },
            },
          },
          "& .MuiButtonBase-root:nth-child(2)": {
            left: "-5px",
            zIndex: 90,
          },
          "& .MuiButtonBase-root:nth-child(3)": {
            left: "-10px",
            zIndex: 80,
            "& .hideCount span": {
              display: "none",
            },
          },
          "& .MuiButtonBase-root:nth-child(4)": {
            left: "-15px",
            zIndex: 70,
          },
          "& .MuiButtonBase-root:nth-child(5)": {
            left: "-20px",
            zIndex: 60,
            "& .hideCount span": {
              display: "none",
            },
          },
          "& .MuiButtonBase-root:nth-child(6)": {
            left: "-25px",
            zIndex: 50,
          },
          "& .MuiButtonBase-root:nth-child(7)": {
            left: "-30px",
            zIndex: 40,
          },
        },
      },
      // Loading indicator
      ".loadingIndicator": {
        marginTop: 20,
        "& .loadingIndicatorText": {
          textAlign: "center",
          marginTop: "5px",
          fontStyle: "italic",
          color: currentColors.primary,
        },
      },
      // Component - Swich
      ".swithcComponent": {
        "& .MuiFormControlLabel-label": {
          color: "#1C1C1C",
          fontSize: 14,
          fontWeight: "bold",
        },
      },
      // Component - blue summary card
      ".componentBlueSummaryCard": {
        backgroundColor: "#C7DFF7",
        borderRadius: 16,
        padding: 15,
        wordWrap: "break-word",
        overflowWrap: "break-word",
        wordBreak: "break-word",

        "& .cardTitle": {
          fontSize: 16,
          fontWeight: "600",
          marginBottom: 10,
          color: styles.text.colorTitle,
        },
        "& .blockSection": {
          color: styles.inputs.colorLabel,
          fontSize: 14,
          overflowWrap: "anywhere",
          wordBreak: "break-word",
          whiteSpace: "normal",
          "&.highlight": {
            "& .row": {
              fontSize: 16,
              fontWeight: "bold",
            },
          },
          "&.withDivider": {
            borderBottom: "1px solid #B3B3B3",
            paddingBottom: 10,
            marginBottom: 10,
          },
          "& .blockTitle": {
            fontWeight: "600",
          },
          "& .row": {
            width: "50%",
            maxWidth: "100%",
            "&:first-of-type": {
              textAlign: "left",
            },
            "&:last-of-type": {
              textAlign: "right",
            },
          },
        },
      },
      // comtainer - flex elements align
      "& .flexContainer": {
        padding: "10px 0px",
        marginBottom: 35,
        display: "flex",
        gap: 15,
        "&.withoutPadding": {
          padding: 0,
        },
        "&.withoutMargin": {
          margin: 0,
        },
        "&.spaceBetween": {
          justifyContent: "space-between",
        },
        "&.flexEnd": {
          justifyContent: "flex-end",
        },
        "&.flexStart": {
          justifyContent: "flex-start",
        },
      },
      // error - helper text and counter container
      "& .inputHelperTextContainer": {
        padding: "1px 5px",
        display: "flex",
        gap: 15,
        fontSize: 12,
        "&.spaceBetween": {
          justifyContent: "space-between",
        },
        "&.flexEnd": {
          justifyContent: "flex-end",
        },
        "&.flexStart": {
          justifyContent: "flex-start",
        },
        "& .helperText.error": {
          color: "#d53737",
        },
        "& .counter": {
          color: "#757575",
          "&.limit": {
            color: "#d53737",
          },
        },
      },

      ".infoTableBordererContainer": {
        marginTop: 30,
        "&.textType": {
          "& td": {
            textAlign: "left",
            "& div": {
              textAlign: "left",
              justifyContent: "flex-start",
            },
          },
        },
        "&.withoutBorders": {
          border: "none",
          "& .infoTableBorderer": {
            border: "none",
          },
          "& td": {
            border: "none",
            fontSize: 14,
          },
          "& td:first-child": {
            border: "none",
            fontWeight: "bold",
            verticalAlign: "top",
          },
        },
        "& .tableTitle": {
          color: "#1C1C1C",
          fontSize: 14,
          fontWeight: "bold",
          marginBottom: 5,
        },
        "& .infoTableBorderer": {
          borderRadius: "16px",
          overflow: "hidden",
          border: "1px solid #B3B3B3",
          "& .MuiTableHead-root": {
            backgroundColor: "#F8F8F8",
            "& th": {
              color: "#1C1C1C",
              fontSize: 14,
              fontWeight: "bold",
              padding: "15px 10px",
            },
          },
          "& th, & td": {
            borderRight: "1px solid #B3B3B3",
            padding: "8px 10px",
          },
          "& th:last-child, & td:last-child": {
            borderRight: "none",
          },

          "& .pricesDetail": {
            fontSize: 14,
            color: "#1C1C1C",
            "& .row": {
              "& .left": {
                textAlign: "right",
                border: "none",
                padding: "0px 5px",
                fontWeight: "bold",
              },
              "& .right": {
                textAlign: "left",
                border: "none",
                padding: "0px 5px",
              },
            },
          },
        },
      },
      ".inputShowInfoContainer": {
        margin: 15,
        "& .inputShowInfo": {
          color: styles.text.colorInputText,
          paddingRight: 10,
          "& .title": {
            fontSize: 14,
            paddingBottom: 3,
            fontWeight: 600,
          },
          "& .detail": {
            fontSize: 16,
          },
        },
      },
      ".MuiButtonBase-root.MuiButton-root": {
        textTransform: "none !important",
      },

      ".statusChip": {
        padding: "6px 10px",
        borderRadius: 15,
        color: "#333333",
        textDecoration: "none",
        position: "relative",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        "&::before": {
          content: '""',
          height: "14px",
          width: "14px",
          backgroundColor: "var(--bg-color-var)",
          display: "block",
          marginRight: "8px",
          borderRadius: "50%",
        },
        "&.primary::before": {
          backgroundColor: "#135265",
        },
        "&.green::before": {
          backgroundColor: "#32BA01",
        },
        "&.orange::before": {
          backgroundColor: "#FF9D00",
        },
        "&.red::before": {
          backgroundColor: "#E51328",
        },
        "&.grey::before": {
          backgroundColor: "#757575",
        },
        "&.blue::before": {
          backgroundColor: "#3269A8",
        },
        "&.blue-grey::before": {
          backgroundColor: "#007C7B",
        },
      },
      // Table List Container - List view
      ".viewListTableListContainer": {
        padding: "5px",
        "& .tableListContainer": {
          borderRadius: 16,
          overflow: "hidden",
          boxShadow: "0px 2px 3px 0px #bdbdbd",
          backgroundColor: "#FFFFFF",
          "& .tableHeader": {
            // border: 'none',
            backgroundColor: "#CAE4F9",
            "& th": {
              fontSize: "12px",
              fontWeight: "700",
              textDecoration: "none",
              color: "#135265",
              paddingTop: 10,
              paddingBottom: 9,
            },
          },
          "& .tableBody": {
            "&:has(tr:not(.MuiTableRow-hover))": {
              "& >:nth-child(4n+3)": {
                backgroundColor: "#f8fafa",
                "&:hover": {
                  backgroundColor: "#3b596314",
                },
              },
            },
            // border: 'none',
            "& >:nth-child(even)": {
              backgroundColor: "#f8fafa",
              "&:hover": {
                backgroundColor: "#3b596314",
              },
            },
            "& .MuiTableCell-root": {
              textDecoration: "none !important",
              padding: "10px 16px",
            },
            "& .MuiTableRow-hover": {
              "& td": {
                height: "50px",
              },
            },
            "& td": {
              fontSize: "12px",
              textDecoration: "none !important",
              "& p": {
                margin: 0,
              },
            },
          },
          "& .tableFooter": {
            "& .MuiTableCell-footer": {
              padding: 0,
            },
          },
          borderCollapse: "collapse",
          "& td": {
            borderRight: "2px solid #ebebeb",
            borderLeft: "2px solid #ebebeb",
          },
          "& th": {
            borderRight: "2px solid #f5fafe",
            borderLeft: "2px solid #f5fafe",
          },
          "& th:last-child, & td:last-child": {
            borderRight: "none",
          },
          "& th:first-child, & td:first-child": {
            borderLeft: "none",
          },
        },
      },

      // Main Body Card
      ".generalModuleCardContainer": {
        // padding: 10,

        "& .headerModuleTitle": {
          margin: "0 5px 10px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "15px 0",
          // borderBottom: '1px solid #B3B3B3',
          position: "relative",
          zIndex: 90,
          "&.withDivider": {
            borderBottom: "1px solid #B3B3B3",
            paddingBottom: 24,
            marginBottom: 20,
          },
          "& .title": {
            fontWeight: "500",
            color: "#1C1C1C",
            fontSize: 16,
          },
          "& .paragrah": {
            fontSize: "0.875rem",
            color: "#757575",
            paddingRight: "16px",
          },
          "& .MuiButtonBase-root": {
            textTransform: "none",
          },
					'& .leftBlock': {
						paddingRight: '10px',
					},
          "& .rightBlock": {
            minWidth: "400px",
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 10,
          },
        },
        "& .mainBodyModulePaper": {
          padding: 15,
          marginBottom: 15,
          borderRadius: 16,
          border: "1px solid #ededed",
          backgroundColor: "#ffffff",
          transition: "background-color 0.4s ease",
        },
        // SECTIONS component
        "& .sectionComponent": {
          marginBottom: 25,
          width: "100%",
          "&:has(.counter)": {
            marginBottom: "8px",
          },
          // styles while loading disabled
          "&.loading": {
            backgroundColor: "#fbfbfb",
          },
          // styles inputs on Filters section
          "&.filters": {
            marginBottom: 5,
            "& .smallTitle": {
              color: "#757575",
            },
          },
          "&.withoutPadding": {
            padding: 0,
            margin: 0,
          },
          // styles inputs on Filters section
          "&.withBackground": {
            backgroundColor: "#eeeeee",
            padding: 15,
            borderRadius: 8,
          },
          "&.withTopDivider": {
            borderTop: "1px solid #B3B3B3",
            paddingTop: 24,
            marginTop: 24,
          },
          "&.withDivider": {
            borderBottom: "1px solid #B3B3B3",
            paddingBottom: 24,
            marginBottom: 24,
          },
          "&.withBorder": {
            border: "1px solid #B3B3B3",
            borderRadius: 8,
            padding: 10,
          },
          "& .title": {
            fontSize: "16px",
            color: styles.text.colorTitle,
            fontWeight: 500,
            marginBottom: 10,
            "&.colorPrimary": {
              color: currentColors.primary,
            },
          },
          "& .label": {
            fontSize: "14px",
            color: styles.inputs.colorLabel,
            fontWeight: 500,
          },
          "& .subtitle": {
            fontSize: "14px",
            margin: "5px 0px",
          },
          "& .infoParagraph": {
            fontSize: "12px",
            margin: "5px 0px",
            fontStyle: "italic",
            display: "flex",
            alignItems: "initial",
            "& .material-symbols-outlined": {
              color: currentColors.primary,
              fontSize: "16px",
              marginRight: "4px",
            },
          },
          "& .input": {
            marginBottom: 5,
          },
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: styles.inputBorderColor,
          },
          "& .setDisabled": {
            //borderWidth: '0px !important',
            "& .MuiOutlinedInput-root.Mui-disabled": {
              backgroundColor: "#f7f7f7  !important",
              //borderWidth: '0px !important',
              "& .MuiOutlinedInput-notchedOutline": {
                //borderWidth: '0px !important',
              },
            },
            "& .MuiInputBase-root.Mui-disabled input": {
              borderWidth: "0px !important",
              "-webkit-text-fill-color": "#434343",
            },
            "& .MuiOutlinedInput-notchedOutline": {
              borderWidth: "0px !important",
              "-webkit-text-fill-color": "#434343",
            },
            "&.html-wrapper-class.disabled .html-editor-class": {
              color: "#434343",
            },
          },
          "& .setDisabledTextField": {
            backgroundColor: "#f7f7f7  !important",
          },
        },
      },
      // Product module Create container
      ".moduleProductCreateContainer": {
        padding: 10,
        "& .moduleTitle": {
          fontSize: "16px",
          display: "flex",
          alignItems: "center",
          color: "#1E1E1E",
          marginBottom: 5,
          minHeight: 53,
          "& .number": {
            width: "33px",
            height: "33px",
            borderRadius: "50%",
            backgroundColor: "#D9D9D9",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: "bold",
            textAlign: "center",
          },
          "& .text": {
            fontWeight: 600,
            marginLeft: 15,
          },
        },

        "& .stepsProgressContainer": {
          borderRadius: 16,

          "& .linearProgress": {
            backgroundColor: "#F8F8F8",
            borderRadius: "16px 16px 0px 0px",
            padding: 15,
            "& .percentageText": {
              fontSize: "14px",
              color: "#1E1E1E",
              display: "flex",
              justifyContent: "space-between",
              "& .column": {
                display: "flex",
                alignItems: "center",
              },
            },
          },
          "& .itemsProgress": {
            fontSize: "14px",
            padding: 15,
            "&.isSubformExpanded": {
              backgroundColor: "#f5f5f5",
              pointerEvents: "none",
            },
            "& .itemSection": {
              borderBottom: "1px solid #C2C2C2",
              marginBottom: 15,
              paddingBottom: 15,
              "&:last-child": {
                borderBottom: "none",
              },
            },
            "& .checkDone": {
              position: "relative",
              top: "-5px",
              "& .icon": {
                color: "#135265",
              },
            },
            "& .icon": {
              fontSize: 30,
              position: "relative",
              top: "-5px",
            },
            "& .progressSection": {
              color: "#979797c7",
              "& .section": {
                color: "#1E1E1E",
                fontWeight: 600,
              },
              "& .subSection": {
                paddingLeft: 0,
                listStyle: "none",
                marginTop: 5,
                "& li": {
                  paddingLeft: "20px",
                  position: "relative",
                  marginBottom: "5px",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    left: 0,
                    top: "12px",
                    transform: "translateY(-50%)",
                    height: "6px",
                    width: "6px",
                    backgroundColor: "#979797c7",
                    borderRadius: "50%",
                    transition: "all 0.2s ease-in-out",
                  },
                },
                "& .clickable": {
                  cursor: "pointer",
                  color: "#135265",
                  "&:hover": {
                    color: "#2c4d57",
                    textDecoration: "underline",
                  },
                },
                "& li.active": {
                  "&::before": {
                    height: "8px",
                    width: "8px",
                    backgroundColor: "#135265",
                  },
                },
              },
            },
          },
        },

        "& .mainBodyModulePaper": {
          "& .sectionComponent": {
            "& .componentRadioButton:has(.image)": {
              "& :has(.Mui-disabled)": {
                "& img, .labels": {
                  opacity: "0.5",
                },
              },
              "& .MuiStack-root": {
                alignItems: "center",
                marginBottom: "14px",
                "& .labels": {
                  marginBottom: "2px",
                  "& .title": {
                    marginTop: "0",
                  },
                },
                "& .MuiButtonBase-root": {
                  paddingTop: "9px",
                },
                "& > :not(style) ~ :not(style)": {
                  marginLeft: "4px",
                  marginRight: "8px",
                },
                "& .image, .button": {
                  display: "flex",
                  paddingTop: "0",
                  "& img": {
                    width: "56px",
                  },
                },
              },
            },
          },
        },
      },
      // Booking module container
      ".moduleBookingContainer": {
        "& .refundButton": {
          backgroundColor: "#8b5cf6",

          "&:hover": {
            backgroundColor: "#744cce",
          },
        },
      },
      // Input with Select together
      "& .componenteInputSelect": {
        padding: "0px !important",
        display: "flex",
        width: "100%",
        marginTop: 5,
        "& .inputComponent": {
          flex: 1,
          "& .MuiFormControl-root": {
            marginTop: "0px !important",
          },
          "& .MuiInputBase-root": {
            height: 44,
            "& .MuiOutlinedInput-notchedOutline": {
              borderRadius: "8px 0 0 8px",
            },
          },
        },
        "& .selectComponent": {
          width: 120,
          height: 44,
          "& .MuiOutlinedInput-notchedOutline": {
            borderRadius: "0 8px 8px 0",
          },
        },
      },
      // Account general module container
      ".moduleAccountGeneralContainer": {
        "& .phoneInputContainer": {
          padding: "0px !important",
          display: "flex",
          width: "100%",
          marginTop: 5,

          "& .prefixInput": {
            width: 120,
            height: 44,

            "& .MuiOutlinedInput-notchedOutline": {
              borderRadius: "8px 0 0 8px",
            },
          },

          "& .phoneInput": {
            flex: 1,

            "& .MuiInputBase-root": {
              height: 44,

              "& .MuiOutlinedInput-notchedOutline": {
                borderRadius: "0 8px 8px 0",
              },
            },
          },
        },
      },
      // Mapping - POI List container
      ".mappingPointsList": {
        "& tr": {
          "& td": {
            fontSize: 14,
            color: "#333333",
            verticalAlign: "sub",
          },
        },
        "& .mappingTitle": {
          marginBottom: 8,
          fontWeight: 600,
        },
        "& .mappingDescription": {
          color: "#4B5563",
          marginBottom: 16,
        },
        "& .mappingSectionCaption": {
          display: "block",
          fontWeight: 600,
          textTransform: "uppercase",
          color: "#374151",
          marginBottom: 8,
        },
        "& .mappingAreaItem": {
          display: "flex",
          alignItems: "center",
          paddingTop: 8,
          paddingBottom: 8,
        },
        "& .mappingAreaIndex": {
          display: "inline-flex",
          width: 28,
          height: 28,
          borderRadius: "50%",
          background: "#B3B3B3",
          color: "#fff",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 14,
          fontWeight: 600,
          marginRight: 12,
        },
        "& .mappingPoiIndexWrapper": {
          position: "relative",
          width: 23,
          height: 23,
          marginRight: 12,
          display: "inline-flex",
        },
        "& .mappingPoiIndexBg": {
          position: "absolute",
          inset: 0,
          background: "#B3B3B3",
          transform: "rotate(45deg)",
          borderRadius: 0,
        },
        "& .mappingPoiIndexText": {
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 14,
          fontWeight: 600,
          color: "#fff",
        },
        "& .mappingAreaName": {
          // body1 default; keep color/default
        },
        "& .mappingMapPlaceholder": {
          minHeight: 420,
          border: "2px dashed #D1D5DB",
          borderRadius: 8,
          background: "#F9FAFB",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#6B7280",
          fontSize: 14,
          fontStyle: "italic",
        },
      },
      // Account finance module container
      ".moduleAccountFinanceContainer": {
        "& .defaultInputContainer": {
          display: "flex",
          alignItems: "center",

          "& .switchComponent": {
            height: 51,
          },
        },

        "& .MuiFormControl-root": {
          marginTop: 5,

          "& .MuiInputBase-root": {
            height: 44,
          },

          "& .selectComponent": {
            marginTop: 0,
          },
        },
      },
      // Component - String With Select
      ".componenteStringWithSelect": {
        display: "flex",
        "& .inputComponent": {
          "& .MuiOutlinedInput-root": {
            borderRadius: "8px 0px 0px 8px !important",
          },
        },
        "& .selectComponent ": {
          borderRadius: "0px 8px 8px 0px !important",
          height: 44,
          marginTop: 5,
          "& .MuiOutlinedInput-root": {
            borderRadius: "8px 0px 0px 8px !important",
          },
        },
      },
      // Component - Text Editor html
      ".componenteTextEditor": {
        "& .rdw-editor-toolbar": {
          paddingLeft: 0,
          border: "none",
          boxShadow: "inherit",

          "& .rdw-option-active": {
            boxShadow: "inherit",
            backgroundColor: "#1552642e",
          },

          "& .rdw-option-wrapper": {
            borderRadius: 4,
            border: "1px solid #818181",
          },
        },
      },
      ".html-wrapper-class": {
        "&.disabled": {
          border: "1px solid #d6d6d6",
          borderRadius: 8,

          "& .html-editor-class": {
            borderColor: "#00000042",
            backgroundColor: "#f5f5f5",
            color: "#00000061",
            cursor: "default",
            padding: 10,
          },
        },

        "& .rdw-editor-toolbar": {
          paddingLeft: 0,
          border: "none",
          boxShadow: "inherit",

          "& .rdw-option-active": {
            boxShadow: "inherit",
            backgroundColor: "#1552642e",
          },

          "& .rdw-option-wrapper": {
            borderRadius: 4,
            border: "1px solid #818181",
          },
        },

        "& .html-editor-class": {
          padding: "12px 14px",
          border: "1px solid #757575",
          borderRadius: 8,
          cursor: "text",
          fontSize: "0.86rem",
          color: "#757575",
          pointerEvents: "none",

          "&.error": {
            borderColor: "#d32f2f",
          },

          "&.focused": {
            borderWidth: 2,
          },

          "& .DraftEditor-root": {
            pointerEvents: "auto",
          },

          "& .DraftEditor-root, & .DraftEditor-editorContainer, & .public-DraftEditor-content":
            {
              minHeight: 130,
            },

          "& .public-DraftStyleDefault-block": {
            margin: 0,
            lineHeight: 1.5,
          },
        },
      },
      // Component - Time Picker With Range
      ".MuiTimePickerToolbar-root": {
        "& .MuiTimePickerToolbar-ampmLabel.Mui-selected": {
          color: "#135265",
          fontWeight: 600,
        },
      },
      // Component - Time Picker Fields
      ".componentTimePickerField": {
        "& .MuiInputBase-root": {
          height: 44,
          borderRadius: 8,
        },
        "& .MuiInputBase-root.Mui-disabled": {
          "& .insideLabel": {
            marginRight: 10,
          },
        },
      },
      // Component - Mapping Component
      ".componentMappingComponent": {
        display: "flex",
        flexDirection: "column",
        position: "relative",
        paddingTop: "8px",

        "& .mappingWrapper": {
          flex: 1,
          display: "flex",
          transition: "opacity 0.2s ease, filter 0.2s ease",
        },
        "& .mappingWrapper.readOnly": {
          filter: "grayscale(85%)",
          opacity: 0.8,
        },

        // Left sidebar
        "& .mappingSidebar": {
          width: "30vw",
          borderRight: "1px solid rgba(0, 0, 0, 0.12)",
          display: "flex",
          flexDirection: "column",
          backgroundColor: "#fff",
          overflow: "auto",
          maxHeight: "60vh",
          direction: "rtl",
          "& >div": {
            direction: "ltr",
          },

          // MappingComponent list item states (transitions + selection/current outlines)
          "& .listItem": {
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "8px 16px",
            borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
            backgroundColor: "transparent",
            cursor: "pointer",
            transition:
              "background-color 120ms ease, border-left-color 120ms ease, outline-color 120ms ease",
            "&:hover": {
              backgroundColor: "rgba(0, 0, 0, 0.04)",
            },
            "&.selected": {
              backgroundColor: "rgba(25, 118, 210, 0.08)",
            },

            // selected: mapped/enabled (uses MUI success[700])
            "&.selected.mapped": {
              borderLeft: "3px solid #2e7d32",
              backgroundColor: "rgba(46, 125, 50, 0.06)",
            },

            // current: actively selected in the UI (outline)
            "&.current": {
              outline: "2px dashed #1976d2",
              outlineOffset: "-2px",
            },

            // both: blend selected + current
            "&.selected.current, &.selected.current.mapped": {
              backgroundColor: "rgba(46, 125, 50, 0.08)",
              outlineColor: "#1976d2",
            },

            "&.selected:hover": {
              // ensure hover doesn't override selected visual
              backgroundColor: "rgba(46, 125, 50, 0.06)",
            },
          },
        },

        // Section headers (Areas/Pools & POIs)
        "& .sectionHeader": {
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "12px",
          borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
          backgroundColor: "rgba(0, 0, 0, 0.04)",
          cursor: "pointer",
          "& .MuiIconButton-root": {
            padding: 0,
          },
          "& .sectionTitle": {
            flex: 1,
            fontWeight: 600,
            fontSize: "0.875rem",
          },
          "& .sectionCount": {
            fontSize: "0.875rem",
            color: "rgba(0, 0, 0, 0.6)",
          },
        },

        // Item badges (numbered circles/squares)
        "& .itemBadge": {
          width: 28,
          height: 28,
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "0.875rem",
          fontWeight: 600,
          flexShrink: 0,
          "&.product": {
            borderRadius: 4,
            backgroundColor: "#1976d2",
          },
          // mapped/completed state (green)
          "&.product.mapped": {
            backgroundColor: "#5da360ff",
          },
          "&.product.inactive": {
            backgroundColor: "#9e9e9e",
          },
          "&.area": {
            borderRadius: 4,
            backgroundColor: "#1976d2",
          },
          "&.area.inactive": {
            backgroundColor: "#9e9e9e",
          },
          "&.poi": {
            borderRadius: "50%",
            backgroundColor: "#9c27b0",
          },
          "&.poi.inactive": {
            backgroundColor: "#9e9e9e",
          },
        },

        // Item name text
        "& .itemName": {
          flex: 1,
          fontSize: "0.875rem",
        },
        "& .itemNameInput": {
          flex: 1,
        },

        // Add button container
        "& .addButtonContainer": {
          padding: 8,
        },

        // Right image panel
        "& .mappingImagePanel": {
          flex: 1,
          display: "flex",
          flexDirection: "column",
          backgroundColor: "#f5f5f5",
        },
        "& .imageContainer": {
          //flex: 1,
          position: "relative",
          overflow: "hidden",
          maxHeight: "55vh",
          width: "fit-content",
          margin: "0 auto",
          "&.clickable": {
            cursor: "crosshair",
          },
          "& img": {
            width: "100%",
            height: "100%",
            objectFit: "contain",
            display: "block",
            userSelect: "none",
            pointerEvents: "none",
          },
        },
        // Map points (areas and POIs)
        "& .mapPoint": {
          position: "absolute",
          transform: "translate(-50%, -50%)",
          width: 32,
          height: 32,
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "0.875rem",
          fontWeight: 700,
          pointerEvents: "auto",
          transition: "all 0.1s",
          "&.product": {
            borderRadius: 4,
            backgroundColor: "#5da360ff",
            border: "2px solid #fff",
            boxShadow: "0px 2px 4px rgba(0,0,0,0.2)",
          },
          "&.area": {
            borderRadius: 4,
            backgroundColor: "#1976d2",
            border: "2px solid #fff",
            boxShadow: "0px 2px 4px rgba(0,0,0,0.2)",
          },
          "&.poi": {
            borderRadius: "50%",
            backgroundColor: "#9c27b0",
            border: "2px solid #fff",
            boxShadow: "0px 2px 4px rgba(0,0,0,0.2)",
          },
          "&.selected": {
            border: "3px solid #fff",
            boxShadow: "0px 4px 8px rgba(0,0,0,0.3)",
          },
          "&.draggable": {
            cursor: "move",
            userSelect: "none",
          },
          "&:hover": {
            boxShadow: "0px 4px 8px rgba(0,0,0,0.3)",
            transform: "translate(-50%, -50%) scale(1.1)",
          },
        },

        // Instructions footer
        "& .instructionsFooter": {
          padding: 12,
          // backgroundColor: '#e3f2fd',
          color: "#01579b",
          borderTop: "1px solid rgba(0, 0, 0, 0.12)",
          "& .MuiTypography-root": {
            fontSize: "0.750rem",
          },
        },
      },
      // Component - Just Numbers
      ".componentJustNumbers": {
        "& .MuiInputBase-root": {
          height: 44,
          borderRadius: 8,
        },
        "& .MuiInputBase-root.Mui-disabled": {
          "& .endAdornment": {
            marginRight: 10,
          },
        },
      },
      // Component - Checkbox / Radiobutton
      ".componentCheckbox, .componentRadioButton": {
        marginTop: 8,
        "& .MuiButtonBase-root": {
          paddingTop: 3,
        },
        "& .MuiStack-root": {
          marginBottom: 5,
        },
        "& .labels": {
          fontSize: 14,
          marginLeft: 5,
          width: "100%",
          "& .title": {
            marginTop: 3,
            marginBottom: 0,
          },
          "& .subtitle": {
            marginTop: 0,
            marginBottom: 0,
          },
          "& .subtitle2": {
            fontStyle: "italic",
            "& .important": {
              fontWeight: 600,
            },
          },
        },
        "& .button": {
          paddingTop: 3,
        },
        "& .text": {
          width: "100%",
        },
        "& .image": {
          paddingTop: 4,
        },
      },
      // Component -  DateFrom To
      ".componentDateFromTo": {
        "& .labelDate": {
          color: "#1E1E1E",
          fontSize: 12,
        },
      },
      // Accessibility Section - Cards list table
      ".accessibilitySectionCards": {
        "& .containerAccessibilitySectionCards": {
          border: "1px solid #e0e0e0",
          borderRadius: "8px",
          maxHeight: "300px",
          overflowY: "auto",
          "& .header": {
            display: "grid",
            gridTemplateColumns: "1fr 80px 80px",
            gap: theme.spacing(1),
            padding: theme.spacing(2),
            borderBottom: "1px solid #e0e0e0",
            backgroundColor: "#f5f5f5",
            fontWeight: 500,
            position: "sticky",
            top: 0,
            zIndex: 1,
            alignItems: "center",
          },
          "& .row": {
            display: "grid",
            gridTemplateColumns: "1fr 80px 80px",
            gap: theme.spacing(1),
            padding: theme.spacing(2),
            alignItems: "center",
            "&:not(:last-child)": {
              borderBottom: "1px solid #e0e0e0",
            },
            "&:nth-of-type(odd)": {
              backgroundColor: "#f9f9f9",
            },
            "&:nth-of-type(even)": {
              backgroundColor: "white",
            },
          },
          "& .labelDate": {},
        },
      },
      // Component - Select
      ".tableSimpleGeneralStyle": {
        "& tr": {
          "& td": {
            fontSize: 14,
            color: "#333333",
            verticalAlign: "sub",
          },
        },
      },
      // Component - Select
      ".componentSelect": {
        "& .MuiSelect-select": {
          "& .title": {
            color: "#1E1E1E",
            fontWeight: "normal",
          },
        },
        "& em": {
          color: "#b1b1b1",
        },
        "& .MuiSvgIcon-root": {
          marginRight: "8px",
          pointerEvents: "none",
          position: "absolute",
          right: 0,
        },
        "& .selectComponent": {
          borderRadius: 8,
          height: 44,
          marginTop: 5,

          "&.isPlaceholder .MuiSelect-select": {
            color: "#b1b1b1",
            fontSize: 13,
          },
        },
      },
      ".MuiMenuItem-root.componentSelectOption": {
        padding: "6px 16px",

        "& .MuiCheckbox-root": {
          padding: "0 9px 0 0",
        },

        "& .columnSystem": {
          display: "flex",
          width: "100%",

          "&.noSeparateColumns .column": {
            flex: "none",
          },

          "& .column": {
            flex: 1,
            paddingRight: 5,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          },
        },
      },
      // Component - Cards items created
      ".itemsCreatedToEditDelete": {
        // individual card item
        "& .itemCreated": {
          padding: "10px 15px",
          fontSize: 14,
          marginBottom: 10,
          alignItems: "center",
          backgroundColor: "#EEEEEE",
          borderRadius: 8,
          color: "#333333",
          "&.withoutBackground": {
            backgroundColor: "transparent",
          },
          "&.formatRows": {
            padding: "5px 10px",
            fontSize: 14,
            "& .rowHeader": {
              display: "flex",
              justifyContent: "space-between",
              "& .title": {
                fontSize: 14,
                fontWeight: 600,
              },
            },
            "& .rowDescription": {
              paddingBottom: 5,
              "&.subItem": {
                fontStyle: "italic",
              },
            },
          },
          "&.formatTable": {
            display: "flex",
            padding: "10px 15px",
            justifyContent: "space-between",
            "& .column": {
              width: "25%",
              textAlign: "center",
            },
            "& .columnDescription": {
              width: "25%",
              textAlign: "left",
              "& .title": {
                display: "block",
                fontSize: 14,
              },
              "& .subtitle": {},
            },
            "&.tableHeader": {
              color: styles.colors.lightGrey,
              padding: "0px 10px",
            },
            "& .columnActions": {
              width: "25%",
              textAlign: "right",
            },
          },
        },
        "& .itemEmpty": {
          padding: "10px 15px",
          fontSize: 14,
          marginBottom: 10,
          alignItems: "center",
          fontStyle: "italic",
          borderRadius: 10,
          border: "1px dashed #cdcdcd",
          background: "#eeeeee1f",
        },
      },
      ".sectionDivider": {
        border: "1px solid #B3B3B3",
        marginBottom: 24,
      },
      // Component - Create new element
      ".componentSectionCreateNew": {
        marginBottom: 15,
        "& .MuiButtonBase-root": {},
        "& .newSectionContainer": {
          border: "1px solid #135265",
          borderRadius: 8,
          padding: 15,
          backgroundColor: "#fff",
        },
      },
      // Component - Json View
      ".componentJsonView": {
        border: "1px solid #B3B3B3",
        borderRadius: 8,
        padding: 8,
        "& .react-json-view": {
          borderRadius: 8,
          padding: 5,
          backgorundColor: "rgb(35 35 35)",
        },
      },
      // Component - Json View
      ".componentImageToCrop": {
        border: "3px dashed #757575",
        borderRadius: 10,
        padding: 35,
        textAlign: "center",
        marginTop: 8,
        position: "relative",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        "& .image-preview-container": {
          position: "relative",
          width: "150px",
          height: "150px",
          borderRadius: "10px",
          overflow: "hidden",
          border: "2px solid #ccc",
          display: "flex",
          marginTop: 10,
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#f5f5f5",
          "& .image-wrapper": {
            position: "relative",
            width: "150px",
            height: "150px",
            borderRadius: "10px",
            overflow: "hidden",
            border: "2px solid #ccc",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#f5f5f5",
            "& .close-button": {
              position: "absolute",
              top: 5,
              right: 5,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              color: "white",
              padding: "1px",
              fontSize: "12px",
              cursor: "pointer",
            },
            "& img": {
              width: "100%",
              height: "100%",
              objectFit: "contain",
              borderRadius: "10px",
            },
          },
        },
      },
      //  Users - Permissions and Authorizations table
      ".users": {
        "& .userPermissionsTable": {
          "& th.MuiTableCell-alignCenter": {
            borderLeft: "1px solid #1c657b",
            verticalAlign: "top",
            "& .MuiFormControlLabel-root": {
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              margin: 0,
              "& .MuiCheckbox-root": {
                marginBottom: "2px",
                color: "inherit",
                "&.Mui-checked": {
                  color: "inherit",
                },
              },
              "& .MuiFormControlLabel-label": {
                fontWeight: 600,
                fontSize: "0.875rem",
              },
            },
            "& span": {
              fontSize: "12px",
              fontWeight: 400,
              lineHeight: 1.2,
              marginTop: "2px",
            },
          },
          "& tbody": {
            "& .MuiTableRow-root": {
              "& span": {
                margin: 0,
                "&.MuiFormControlLabel-label": {
                  display: "none",
                },
              },
            },
          },
        },
      },
    },
  })(() => null);

  return (
    <ThemeProvider theme={theme}>
      <GlobalCSS />
      {children}
    </ThemeProvider>
  );
};

export default Theme;
